'use client';

import {
  useCallOperator,
  useCallOperatorControls,
  useCallOperatorCurrentCall,
  useCallOperatorCurrentCallControls,
  useCallOperatorState,
  useCallOperatorSuccessQualifications,
  useCallOperatorDiscardQualifications,
  useCallOperatorAudioInputMuted,
  useCallOperatorAudioOutputMuted,
  useCallOperatorAudioOutputVolume,
  useCallOperatorCurrentCallInfo,
} from '@callixbrasil/client-sdk-react';
import type { Call, CallInteractionResult, CallQualification } from '@callixbrasil/client-sdk';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type FocusEvent,
  type ChangeEvent,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowLeft,
  FiClock,
  FiDelete,
  FiHeadphones,
  FiLogOut,
  FiMic,
  FiMicOff,
  FiPhone,
  FiPhoneCall,
  FiPhoneIncoming,
  FiPhoneOff,
  FiPlus,
  FiMinus,
  FiSettings,
  FiUser,
  FiVolume1,
  FiVolume2,
  FiVolumeX,
  FiX,
  FiCheckCircle,
  FiPauseCircle,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import { tw } from '../lib/twind';
import { CallInfoDisplay } from './CallInfoDisplay';
import { useSignal } from '../hooks/useSignal';
import { useOperatorBreak } from '../hooks/useOperatorBreak';
import { normalizePhoneNumberPaste } from '../lib/phoneUtils';
import { useDialerConfig } from '../widget/index';

const DEFAULT_COUNTRY_PREFIX = '+55';
const MAX_DIAL_LENGTH = 32;

const DEFAULT_DIALER_DIMENSIONS = { width: 448, height: 640 } as const;
const MIN_DIALER_WIDTH = 280;
const MIN_DIALER_HEIGHT = 420;
const MAX_DIALER_WIDTH = 1024;
const MAX_DIALER_HEIGHT = 960;
const MIN_DIALER_SCALE = 0.55;
const MAX_DIALER_SCALE = 2.5;
const VIEWPORT_MARGIN_SMALL = 12;
const VIEWPORT_MARGIN_DEFAULT = 16;

const keypadKeys = [
  { value: '1', detail: '' },
  { value: '2', detail: 'ABC' },
  { value: '3', detail: 'DEF' },
  { value: '4', detail: 'GHI' },
  { value: '5', detail: 'JKL' },
  { value: '6', detail: 'MNO' },
  { value: '7', detail: 'PQRS' },
  { value: '8', detail: 'TUV' },
  { value: '9', detail: 'WXYZ' },
  { value: '*', detail: '' },
  { value: '0', detail: '+' },
  { value: '#', detail: '' },
] as const;

type FloatingDialerProps = {
  isAuthenticated: boolean;
  username: string | null;
  onLogin: (username: string) => Promise<void> | void;
  onLogout: () => void;
  isLoadingSession: boolean;
  sessionError: string | null;
  variant?: 'default' | 'min' | 'mobile';
  portalTarget?: HTMLElement | null;
  mode?: 'floating' | 'inline';  // Não usado diretamente, mas recebido do DialerWrapper
};

type PointerData = {
  pointerId: number | null;
  offsetX: number;
  offsetY: number;
};

type DragPosition = {
  x: number;
  y: number;
};

type Size = {
  width: number;
  height: number;
};

type ResizeEdge = 'right' | 'bottom' | 'corner';

type ResizeData = {
  pointerId: number | null;
  originX: number;
  originY: number;
  startWidth: number;
  startHeight: number;
  edge: ResizeEdge;
};

type NetworkMetricState = {
  rttMs: number | null;
  bitrateKbps: number | null;
  packetLossRatio: number | null;
};

type AudioMetricState = {
  jitterMs: number | null;
  packetLossRatio: number | null;
  audioLevel: number | null;
};

type QualityTone = 'excellent' | 'good' | 'average' | 'poor' | 'unknown';

type QualitySummary = {
  tone: QualityTone;
  label: string;
  message: string;
};

type CallCounterpart = {
  displayName?: string | null;
  phoneNumber?: string | null;
};

type UnknownRecord = Record<string, unknown>;

type MediaDevicesWithOnDeviceChange = MediaDevices & {
  ondevicechange?: ((this: MediaDevices, ev: Event) => unknown) | null;
};

const INITIAL_NETWORK_METRIC: NetworkMetricState = {
  rttMs: null,
  bitrateKbps: null,
  packetLossRatio: null,
};

const INITIAL_AUDIO_METRIC: AudioMetricState = {
  jitterMs: null,
  packetLossRatio: null,
  audioLevel: null,
};

const WEBRTC_STAT_TYPE = {
  candidatePair: 'candidate-pair',
  inboundRtp: 'inbound-rtp',
} as const;
export function FloatingDialer({
  isAuthenticated,
  username,
  onLogin,
  onLogout,
  isLoadingSession,
  sessionError,
  variant = 'default',
  portalTarget = null,
}: FloatingDialerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<PointerData>({ pointerId: null, offsetX: 0, offsetY: 0 });
  const resizeRef = useRef<ResizeData>({
    pointerId: null,
    originX: 0,
    originY: 0,
    startWidth: DEFAULT_DIALER_DIMENSIONS.width,
    startHeight: DEFAULT_DIALER_DIMENSIONS.height,
    edge: 'corner',
  });
  const dragMovedRef = useRef(false);
  const [position, setPosition] = useState<DragPosition>({ x: 24, y: 24 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [rawDialValue, setRawDialValue] = useState('');
  const [manualSize, setManualSize] = useState<Size | null>(null);
  const [observedSize, setObservedSize] = useState<Size>({
    width: DEFAULT_DIALER_DIMENSIONS.width,
    height: DEFAULT_DIALER_DIMENSIONS.height,
  });
  const prevAuthRef = useRef(isAuthenticated);

  const dialValue = useMemo(() => sanitizeDialInput(rawDialValue), [rawDialValue]);
  const isMiniMode = variant === 'min';
  const contentScale = useMemo(() => {
    const widthRatio = observedSize.width / DEFAULT_DIALER_DIMENSIONS.width;
    const heightRatio = observedSize.height / DEFAULT_DIALER_DIMENSIONS.height;
    const ratio = Math.min(widthRatio, heightRatio);
    return clamp(ratio, MIN_DIALER_SCALE, MAX_DIALER_SCALE);
  }, [observedSize]);

  const clampSizeToViewport = useCallback(
    (proposed: Size) => {
      if (typeof window === 'undefined') {
        return {
          width: clamp(proposed.width, MIN_DIALER_WIDTH, MAX_DIALER_WIDTH),
          height: clamp(proposed.height, MIN_DIALER_HEIGHT, MAX_DIALER_HEIGHT),
        };
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = viewportWidth < 640 ? VIEWPORT_MARGIN_SMALL : VIEWPORT_MARGIN_DEFAULT;
      const maxWidth = Math.min(MAX_DIALER_WIDTH, viewportWidth - margin * 2);
      const maxHeight = Math.min(MAX_DIALER_HEIGHT, viewportHeight - margin * 2);

      return {
        width: clamp(proposed.width, MIN_DIALER_WIDTH, Math.max(MIN_DIALER_WIDTH, maxWidth)),
        height: clamp(proposed.height, MIN_DIALER_HEIGHT, Math.max(MIN_DIALER_HEIGHT, maxHeight)),
      };
    },
    []
  );

  const clampPositionToViewport = useCallback(
    (proposed: DragPosition) => {
      if (typeof window === 'undefined') {
        return proposed;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const element = containerRef.current;
      const rawWidth = element?.offsetWidth ?? 0;
      const rawHeight = element?.offsetHeight ?? 0;
      const activeSize = manualSize ?? observedSize ?? DEFAULT_DIALER_DIMENSIONS;
      const fallbackWidth = isOpen ? activeSize.width : 80;
      const fallbackHeight = isOpen ? activeSize.height : 80;
      const width = rawWidth > 0 ? rawWidth : fallbackWidth;
      const height = rawHeight > 0 ? rawHeight : fallbackHeight;
      const margin = viewportWidth < 640 ? VIEWPORT_MARGIN_SMALL : VIEWPORT_MARGIN_DEFAULT;

      const minX = margin;
      const minY = margin;
      const maxX = Math.max(minX, viewportWidth - width - margin);
      const maxY = Math.max(minY, viewportHeight - height - margin);

      let nextX = proposed.x;
      let nextY = proposed.y;

      if (viewportWidth < 640 && isOpen) {
        nextX = Math.round((viewportWidth - width) / 2);
        nextY = margin;
      }

      if (viewportHeight < height + margin * 2) {
        nextY = margin;
      }

      return {
        x: clamp(nextX, minX, maxX),
        y: clamp(nextY, minY, maxY),
      };
    },
    [isOpen, manualSize, observedSize]
  );

  const updateDialValue = useCallback((next: SetStateAction<string>) => {
    setRawDialValue((prev) => {
      const resolved = typeof next === 'function' ? (next as (value: string) => string)(prev) : next;
      return sanitizeDialInput(resolved);
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const width = containerRef.current?.offsetWidth ?? 96;
    const height = containerRef.current?.offsetHeight ?? 96;
    const defaultX = window.innerWidth - width - 24;
    const defaultY = window.innerHeight - height - 24;
    setPosition(clampPositionToViewport({ x: defaultX, y: defaultY }));
  }, [clampPositionToViewport]);

  useEffect(() => {
    setPosition((prev) => clampPositionToViewport(prev));
  }, [isOpen, clampPositionToViewport]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleViewportChange = () => {
      setPosition((prev) => clampPositionToViewport(prev));
      setManualSize((prev) => (prev ? clampSizeToViewport(prev) : prev));
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [clampPositionToViewport, clampSizeToViewport]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const element = cardRef.current;
    if (!element) {
      return;
    }

    const applySize = (width: number, height: number) => {
      setObservedSize({ width, height });
    };

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          const { width, height } = entry.contentRect;
          applySize(width, height);
        }
      });
      observer.observe(element);
      return () => observer.disconnect();
    }

    const rect = element.getBoundingClientRect();
    applySize(rect.width, rect.height);
  }, [isOpen, manualSize]);

  useEffect(() => {
    if (isAuthenticated && !prevAuthRef.current) {
      setIsOpen(true);
    }

    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      dragMovedRef.current = true;

      const modalWidth = containerRef.current?.offsetWidth ?? 0;
      const modalHeight = containerRef.current?.offsetHeight ?? 0;
      const nextX = event.clientX - pointerRef.current.offsetX;
      const nextY = event.clientY - pointerRef.current.offsetY;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
      const margin = viewportWidth < 640 ? VIEWPORT_MARGIN_SMALL : VIEWPORT_MARGIN_DEFAULT;
      const maxX = viewportWidth - modalWidth - margin;
      const maxY = viewportHeight - modalHeight - margin;
      const safeMaxX = Math.max(margin, maxX);
      const safeMaxY = Math.max(margin, maxY);

      setPosition({
        x: clamp(nextX, margin, safeMaxX),
        y: clamp(nextY, margin, safeMaxY),
      });
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      setIsDragging(false);
      pointerRef.current = { pointerId: null, offsetX: 0, offsetY: 0 };
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (resizeRef.current.pointerId !== event.pointerId) {
        return;
      }

      event.preventDefault();

      const { edge, startWidth, startHeight, originX, originY } = resizeRef.current;
      let nextWidth = startWidth;
      let nextHeight = startHeight;

      if (edge === 'right' || edge === 'corner') {
        nextWidth = startWidth + (event.clientX - originX);
      }

      if (edge === 'bottom' || edge === 'corner') {
        nextHeight = startHeight + (event.clientY - originY);
      }

      const clamped = clampSizeToViewport({ width: nextWidth, height: nextHeight });
      setManualSize((prev) => {
        if (prev && prev.width === clamped.width && prev.height === clamped.height) {
          return prev;
        }
        return clamped;
      });
    };

    const handlePointerEnd = (event: PointerEvent) => {
      if (resizeRef.current.pointerId !== event.pointerId) {
        return;
      }

      try {
        (event.target as HTMLElement | null)?.releasePointerCapture?.(event.pointerId);
      } catch {
        // ignore release failures
      }

      setIsResizing(false);
      resizeRef.current = {
        ...resizeRef.current,
        pointerId: null,
      };
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [isResizing, clampSizeToViewport]);

  useEffect(() => {
    if (!isOpen) {
      dragMovedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    setPosition((prev) => clampPositionToViewport(prev));
  }, [manualSize, observedSize, clampPositionToViewport]);

  const beginResize = useCallback(
    (edge: ResizeEdge) => (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0 && event.pointerType !== 'touch') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const element = cardRef.current;
      const rect = element?.getBoundingClientRect();
      const startWidth = rect?.width ?? (manualSize?.width ?? DEFAULT_DIALER_DIMENSIONS.width);
      const startHeight = rect?.height ?? (manualSize?.height ?? DEFAULT_DIALER_DIMENSIONS.height);

      resizeRef.current = {
        pointerId: event.pointerId,
        originX: event.clientX,
        originY: event.clientY,
        startWidth,
        startHeight,
        edge,
      };

      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        // ignore capture errors
      }

      setIsResizing(true);
    },
    [manualSize]
  );

  const beginDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) {
      return;
    }

    const rect = containerRef.current?.getBoundingClientRect();
    pointerRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - (rect?.left ?? 0),
      offsetY: event.clientY - (rect?.top ?? 0),
    };

    dragMovedRef.current = false;
    setIsDragging(true);
  }, []);

  const handleToggle = useCallback(() => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }

    setIsOpen((prev) => !prev);
  }, []);

  const handleLogin = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }

      await onLogin(trimmed);
    },
    [onLogin]
  );

  const dialerContent = isAuthenticated ? (
    <AuthenticatedDialer
      username={username ?? ''}
      dialValue={dialValue}
      setDialValue={updateDialValue}
      onLogout={onLogout}
      variant={variant}
      onRequestClose={() => setIsOpen(false)}
      portalTarget={portalTarget}
    />
  ) : (
    <LoginForm
      defaultValue={username ?? ''}
      onSubmit={handleLogin}
      isLoading={isLoadingSession}
      error={sessionError}
    />
  );

  if (isMiniMode) {
    return dialerContent;
  }

  const scale = Number.isFinite(contentScale) && contentScale > 0 ? contentScale : 1;
  const scaledWidth = DEFAULT_DIALER_DIMENSIONS.width / scale;
  const scaledHeight = DEFAULT_DIALER_DIMENSIONS.height / scale;

  const cardStyle: CSSProperties = {
    width: manualSize ? `${manualSize.width}px` : undefined,
    height: manualSize ? `${manualSize.height}px` : undefined,
    minWidth: `${MIN_DIALER_WIDTH}px`,
    minHeight: `${MIN_DIALER_HEIGHT}px`,
    maxWidth: `min(${MAX_DIALER_WIDTH}px, calc(100vw - 1.5rem))`,
    maxHeight: `calc(100vh - 3rem)`,
  };
  const scaledWrapperStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    width: `${scaledWidth}px`,
    minHeight: `${scaledHeight}px`,
  };
  const cardContentStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: `${DEFAULT_DIALER_DIMENSIONS.width}px`,
    minHeight: `${DEFAULT_DIALER_DIMENSIONS.height}px`,
  };

  return (
    <div
      ref={containerRef}
      className={['callix', 'mode', 'full', tw`fixed z-50 select-none`].join(' ')}
      data-callix-variant="default"
      style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        top: 0,
        left: 0,
      }}
    >
      {isOpen ? (
        <div
          ref={cardRef}
          data-floating-dialer-card="true"
          className={tw`relative flex flex-col items-start overflow-hidden rounded-3xl border border-slate-200/60 bg-gradient-to-br from-white/95 via-slate-50/95 to-white/90 shadow-2xl backdrop-blur-lg transition-all duration-300 w-[min(32rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] sm:w-[28rem] md:w-[30rem] max-h-[calc(100vh-3rem)] sm:max-h-none`}
          style={cardStyle}
        >
          <div className={tw`flex flex-1 flex-col overflow-hidden`} style={scaledWrapperStyle}>
            <div
              data-floating-dialer-content
              className={tw`flex flex-1 flex-col rounded-3xl overflow-hidden`}
              style={cardContentStyle}
            >
              <div
                className={tw`cursor-grab touch-none active:cursor-grabbing flex items-center justify-between bg-gradient-to-r from-primary to-primaryDark px-4 py-3 text-white shadow-sm`}
                onPointerDown={beginDrag}
              >
              <div className={tw`flex items-center gap-3`}>
                <span className={tw`inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 shadow-inner`}>
                  <FiPhoneCall className={tw`text-xl`} />
                </span>
                <div className={tw`space-y-0.5`}>
                  <p className={tw`text-[11px] uppercase tracking-[0.2em] text-white/70`}>Discadora</p>
                  <p className={tw`text-base font-semibold`}>{username ?? 'Visitante'}</p>
                </div>
              </div>
              <button
                type="button"
                className={tw`rounded-full p-2 text-white/80 transition hover:bg-white/15 hover:text-white`}
                onClick={() => setIsOpen(false)}
                aria-label="Fechar discadora"
              >
                <FiX className={tw`text-lg`} />
              </button>
            </div>
            <div className={tw`flex-1 overflow-y-auto p-3`}>{dialerContent}</div>
          </div>
        </div>
        <div
            role="presentation"
            data-floating-dialer-resize="right"
            aria-hidden="true"
            className={tw`absolute right-0 top-16 bottom-12 w-3 cursor-ew-resize touch-none rounded-md border border-transparent transition-colors duration-150 hover:border-primary/30 hover:bg-primary/10`}
            onPointerDown={beginResize('right')}
          />
          <div
            role="presentation"
            data-floating-dialer-resize="bottom"
            aria-hidden="true"
            className={tw`absolute left-16 right-12 bottom-0 h-3 cursor-ns-resize touch-none rounded-md border border-transparent transition-colors duration-150 hover:border-primary/30 hover:bg-primary/10`}
            onPointerDown={beginResize('bottom')}
          />
          <div
            role="presentation"
            data-floating-dialer-resize="corner"
            className={tw`absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center cursor-nwse-resize touch-none`}
            onPointerDown={beginResize('corner')}
            aria-label="Redimensionar discadora"
          >
            <span className={tw`h-3 w-3 rotate-45 rounded-sm border border-slate-300 bg-white/80 shadow`}></span>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onPointerDown={beginDrag}
          onClick={handleToggle}
          className={tw`flex h-16 w-16 items-center justify-center rounded-full bg-primary text-white shadow-xl transition-transform hover:scale-105 hover:bg-primaryDark focus:outline-none focus:ring-4 focus:ring-primary/30`}
          aria-label="Abrir discadora"
        >
          <FiPhone className={tw`text-2xl`} />
        </button>
      )}
    </div>
  );
}

// Remaining component and helper function implementations continue from here...
type LoginFormProps = {
  defaultValue: string;
  onSubmit: (value: string) => Promise<void> | void;
  isLoading: boolean;
  error: string | null;
};

function LoginForm({ defaultValue, onSubmit, isLoading, error }: LoginFormProps) {
  const [value, setValue] = useState(defaultValue);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!value.trim()) {
      return;
    }

    await onSubmit(value);
  };

  const showValidationError = touched && !value.trim();

  return (
    <form className={tw`space-y-4`} onSubmit={handleSubmit}>
      <div className={tw`space-y-2`}>
        <label className={tw`flex items-center gap-2 text-sm font-medium text-slate-700`} htmlFor="dialer-username">
          <FiUser className={tw`text-base text-primary`} />
          Usuario
        </label>
        <input
          id="dialer-username"
          type="text"
          placeholder="Digite seu usuario Callix"
          className={tw`w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40`}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => setTouched(true)}
          disabled={isLoading}
        />
        {showValidationError && (
          <p className={tw`text-xs text-red-500`}>Informe um usuario para continuar.</p>
        )}
        {error && <p className={tw`text-xs text-red-500`}>{error}</p>}
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className={tw`flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-white shadow-lg transition hover:bg-primaryDark disabled:cursor-not-allowed disabled:opacity-60`}
      >
        {isLoading && (
          <span className={tw`inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white`} />
        )}
        Entrar
      </button>
    </form>
  );
}
type AuthenticatedDialerProps = {
  username: string;
  dialValue: string;
  setDialValue: Dispatch<SetStateAction<string>>;
  onLogout: () => void;
  variant: 'default' | 'min' | 'mobile';
  onRequestClose?: () => void;
  portalTarget?: HTMLElement | null;
};

function AuthenticatedDialer({
  username,
  dialValue,
  setDialValue,
  onLogout,
  variant,
  onRequestClose,
  portalTarget = null,
}: AuthenticatedDialerProps) {
  const isMiniMode = variant === 'min';

  // Configuração do dialer (skipQualification para chamadas manuais)
  const { skipManualQualification } = useDialerConfig();

  console.log('[FloatingDialer/AuthenticatedDialer] 🔧 skipManualQualification:', skipManualQualification);

  // Hooks do SDK
  const operator = useCallOperator();
  const operatorState = useCallOperatorState();
  const currentCall = useCallOperatorCurrentCall();
  const currentCallInfo = useCallOperatorCurrentCallInfo();
  const rawSuccessQualifications = useCallOperatorSuccessQualifications();
  const rawDiscardQualifications = useCallOperatorDiscardQualifications();
  const successQualifications = useMemo(() => rawSuccessQualifications ?? [], [rawSuccessQualifications]);
  const discardQualifications = useMemo(() => rawDiscardQualifications ?? [], [rawDiscardQualifications]);
  
  const { makeManualCall, becomeAvailable, finishAfterCall } = useCallOperatorControls();
  const { hangup, answer, reject } = useCallOperatorCurrentCallControls();
  
  const [inputMuted, setInputMuted] = useCallOperatorAudioInputMuted();
  const [outputMuted, setOutputMuted] = useCallOperatorAudioOutputMuted();
  const [outputVolume, setOutputVolume] = useCallOperatorAudioOutputVolume();
  
  // Debug: Log operator state changes
  useEffect(() => {
    console.log('[FloatingDialer] Estado do operador mudou:', {
      state: operatorState?.state,
      operatorState,
      hasCall: !!currentCall,
      currentCall
    });
  }, [operatorState, currentCall]);
  
  // Debug: Log qualifications
  useEffect(() => {
    console.log('[FloatingDialer] Qualificações:', {
      success: successQualifications,
      discard: discardQualifications,
      callState: operatorState?.state
    });
  }, [successQualifications, discardQualifications, operatorState?.state]);
  
  // Debug: Log when receiving incoming call
  useEffect(() => {
    if (currentCall?.direction === 'incoming') {
      console.log('[FloatingDialer] 📞 CHAMADA RECEBIDA:', {
        call: currentCall,
        direction: currentCall.direction,
        state: operatorState?.state,
        counterpart: extractCounterpart(currentCall)
      });
    }
  }, [currentCall, operatorState?.state]);

  const [isDialerFocused, setIsDialerFocused] = useState(false);
  const dialerCardRef = useRef<HTMLDivElement | null>(null);
  const dialDisplayRef = useRef<HTMLDivElement | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const visibleInputRef = useRef<HTMLInputElement | null>(null);

  const [callError, setCallError] = useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [afterCallError, setAfterCallError] = useState<string | null>(null);
  const [isSubmittingAfterCall, setIsSubmittingAfterCall] = useState(false);
  const [afterCallResult, setAfterCallResult] = useState<CallInteractionResult>('success');
  const [selectedQualificationId, setSelectedQualificationId] = useState<number | null>(null);
  const [callDurationSeconds, setCallDurationSeconds] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [networkMetric, setNetworkMetric] = useState<NetworkMetricState>(INITIAL_NETWORK_METRIC);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [audioMetric, setAudioMetric] = useState<AudioMetricState>(INITIAL_AUDIO_METRIC);
  const toneContextRef = useRef<AudioContext | null>(null);
  const hasRequestedAvailabilityRef = useRef(false);
  const [audioDevices, setAudioDevices] = useState<{ inputs: MediaDeviceInfo[]; outputs: MediaDeviceInfo[] }>({
    inputs: [],
    outputs: [],
  });
  const [selectedInputId, setSelectedInputId] = useState<string | null>(null);
  const [selectedOutputId, setSelectedOutputId] = useState<string | null>(null);
  const [audioDeviceError, setAudioDeviceError] = useState<string | null>(null);
  const [isDevicePanelOpen, setIsDevicePanelOpen] = useState(false);
  const activeInputStreamRef = useRef<MediaStream | null>(null);
  const [lastDialedNumber, setLastDialedNumber] = useState<string | null>(null);
  const [isBreakModalOpen, setIsBreakModalOpen] = useState(false);
  const [breakError, setBreakError] = useState<string | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth <= 640 : false
  );
  const isMobileVariant = variant === 'mobile';
  const isMobileLayout = isMobileVariant || isMobileViewport;

  // Use operator.breaks signal to get available breaks
  const breaks = useSignal(operator?.breaks) || [];
  
  // Use the break management hook
  const { goOnBreak, goOffBreak } = useOperatorBreak();

  const sanitizedDialValue = useMemo(() => dialValue.slice(0, MAX_DIAL_LENGTH), [dialValue]);
  const formattedHintNumber = useMemo(() => formatDialerDisplay(sanitizedDialValue, DEFAULT_COUNTRY_PREFIX), [sanitizedDialValue]);
  const normalizedNumber = useMemo(() => normalizeToE164(sanitizedDialValue, DEFAULT_COUNTRY_PREFIX), [sanitizedDialValue]);

  const callState = operatorState?.state;
  const isIdle = callState === 'idle';
  const isOnBreak = callState === 'onBreak';
  const isIncoming = currentCall?.direction === 'incoming';
  const isInCall = callState === 'callInProgress';
  const isRinging = callState === 'manualCallRinging' || callState === 'callRinging';
  const isConnecting = callState === 'manualCallSetup' || isRinging;
  const canEditNumber = !callState || callState === 'starting' || callState === 'idle' || callState === 'manualCallSetup' || callState === 'onBreak';
  const canUseKeypad = canEditNumber || isInCall || isRinging;
  const isNumberValid = Boolean(normalizedNumber && isValidPhoneNumberSafe(normalizedNumber));

  const requestAvailability = useCallback(
    async (failureMessage: string) => {
      if (typeof becomeAvailable !== 'function') {
        setAvailabilityError(failureMessage);
        return;
      }

      try {
        await Promise.resolve(becomeAvailable());
      } catch (error) {
        console.error(failureMessage, error);
        setAvailabilityError(failureMessage);
      }
    },
    [becomeAvailable]
  );

  useEffect(() => {
    if (!hasRequestedAvailabilityRef.current) {
      hasRequestedAvailabilityRef.current = true;
      void requestAvailability('Nao foi possivel ficar disponivel automaticamente.');
    }
  }, [requestAvailability]);

  useEffect(() => {
    if (callState === 'idle') {
      setAvailabilityError(null);
      setCallError(null);
    }
  }, [callState]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateViewport = () => setIsMobileViewport(window.innerWidth <= 640);

    updateViewport();
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  useEffect(() => {
    if (callState === 'offline') {
      setAvailabilityError('Conexao perdida. Tentando reconectar...');
      void requestAvailability('Conexao perdida. Tentando reconectar...');
    }
  }, [callState, requestAvailability]);

  useEffect(() => {
    if (callState === 'afterCall') {
      // Check if campaign has skipCallQualification enabled
      const shouldSkipCampaignQualification =
        currentCallInfo?.type === 'campaign' &&
        currentCallInfo.info.campaign.skipCallQualification === true;

      if (shouldSkipCampaignQualification) {
        console.log('[FloatingDialer] 🚀 skipCallQualification ativado - finalizando automaticamente');
        // Auto-finish without qualification
        if (typeof finishAfterCall === 'function') {
          void Promise.resolve(
            finishAfterCall({
              result: 'success',
              qualificationId: undefined,
            })
          );
        }
        return;
      }

      // Check if manual call - ALWAYS skip qualification for manual calls
      const isManualCall = currentCallInfo?.type === 'manual';

      if (isManualCall) {
        console.log('[FloatingDialer] 🚀 Chamada MANUAL detectada - finalizando automaticamente (chamadas manuais NUNCA exibem qualificação)');
        // Auto-finish without qualification
        if (typeof finishAfterCall === 'function') {
          void Promise.resolve(
            finishAfterCall({
              result: 'success',
              qualificationId: undefined,
            })
          );
        }
        return;
      }

      // Check if no qualifications configured
      const hasNoQualifications = successQualifications.length === 0 && discardQualifications.length === 0;
      if (hasNoQualifications) {
        console.log('[FloatingDialer] 🚀 Sem qualificações configuradas - finalizando automaticamente');
        if (typeof finishAfterCall === 'function') {
          void Promise.resolve(
            finishAfterCall({
              result: 'success',
              qualificationId: undefined,
            })
          );
        }
        return;
      }

      // Normal qualification flow
      const hasSuccessQualification = successQualifications.length > 0;
      setAfterCallResult(hasSuccessQualification ? 'success' : 'failure');
      setSelectedQualificationId(null);
      setAfterCallError(null);
    }
  }, [callState, successQualifications.length, discardQualifications.length, currentCallInfo, finishAfterCall, skipManualQualification]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (callState !== 'starting') {
      return;
    }

    const timer = window.setTimeout(() => {
      setAvailabilityError((current) => current ?? 'Conexao demorando mais que o esperado. Verifique rede ou credenciais.');
    }, 8000);

    return () => window.clearTimeout(timer);
  }, [callState]);

  useEffect(() => {
    if (callState === 'manualCallRinging' || callState === 'callInProgress') {
      setDialValue('');
    }
    
    if (callState === 'idle') {
      setLastDialedNumber(null);
    }
  }, [callState, setDialValue]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    let cancelled = false;

    const loadDevices = async () => {
      try {
        if (navigator.mediaDevices.getUserMedia) {
          await navigator.mediaDevices.getUserMedia({ audio: true });
        }
      } catch (error) {
        console.warn('Permissao de microfone nao concedida ainda', error);
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) {
          return;
        }

        const inputs = devices.filter((device) => device.kind === 'audioinput');
        const outputs = devices.filter((device) => device.kind === 'audiooutput');
        setAudioDevices({ inputs, outputs });
        setAudioDeviceError(null);
        setSelectedInputId((prev) => prev ?? inputs[0]?.deviceId ?? null);
        setSelectedOutputId((prev) => prev ?? outputs[0]?.deviceId ?? null);
      } catch (error) {
        if (!cancelled) {
          console.error('Listagem de dispositivos falhou', error);
          setAudioDeviceError('Nao foi possivel listar dispositivos de audio. Verifique as permissoes.');
        }
      }
    };

    void loadDevices();

    const handleDeviceChange = () => {
      void loadDevices();
    };

    const mediaDevices: MediaDevices = navigator.mediaDevices;

    if ('addEventListener' in mediaDevices) {
      mediaDevices.addEventListener('devicechange', handleDeviceChange);
    } else if ('ondevicechange' in mediaDevices) {
      (mediaDevices as MediaDevicesWithOnDeviceChange).ondevicechange = handleDeviceChange;
    }

    return () => {
      cancelled = true;
      if ('removeEventListener' in mediaDevices) {
        mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      } else if ('ondevicechange' in mediaDevices) {
        (mediaDevices as MediaDevicesWithOnDeviceChange).ondevicechange = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!currentCall || callState !== 'callInProgress') {
      setCallDurationSeconds(0);
      setNetworkMetric(INITIAL_NETWORK_METRIC);
      setAudioMetric(INITIAL_AUDIO_METRIC);
      return;
    }

    const initialDuration = Math.floor(currentCall.currentDurationSeconds ?? 0);
    setCallDurationSeconds(initialDuration);
    console.log('[FloatingDialer] ?? Timer iniciado:', {
      initialDuration,
      currentDurationSeconds: currentCall.currentDurationSeconds,
      callState
    });

    const timer = window.setInterval(() => {
      const duration = Math.floor(currentCall.currentDurationSeconds ?? 0);
      setCallDurationSeconds(duration);
      if (duration % 5 === 0 && duration > 0) {
        console.log('[FloatingDialer] ?? Timer atualizado:', duration, 'segundos');
      }
    }, 1000);

    return () => {
      console.log('[FloatingDialer] ?? Timer parado');
      window.clearInterval(timer);
    };
  }, [currentCall, callState]);

  useEffect(() => {
    if (!currentCall || callState !== 'callInProgress') {
      return;
    }

    const handleStat = (event: unknown) => {
      const statRecord = (getRecordValue<unknown>(event, 'stat') as Record<string, unknown> | null | undefined) ?? {};
      const statType = typeof statRecord.type === 'string' ? (statRecord.type as string) : undefined;

      if (statType === WEBRTC_STAT_TYPE.candidatePair) {
        const rttSeconds = readNumber(statRecord, 'currentRoundTripTime');
        const bitrate = readNumber(statRecord, 'availableOutgoingBitrate');
        setNetworkMetric((prev) => ({
          rttMs: rttSeconds != null ? rttSeconds * 1000 : prev.rttMs,
          bitrateKbps: bitrate != null ? bitrate / 1000 : prev.bitrateKbps,
          packetLossRatio: prev.packetLossRatio,
        }));
      }

      if (statType === WEBRTC_STAT_TYPE.inboundRtp) {
        const packetsReceived = readNumber(statRecord, 'packetsReceived') ?? 0;
        const packetsLost = readNumber(statRecord, 'packetsLost') ?? 0;
        const totalPackets = packetsReceived + packetsLost;
        const lossRatio = totalPackets > 0 ? packetsLost / totalPackets : null;
        const jitterSeconds = readNumber(statRecord, 'jitter');
        const audioLevel = readNumber(statRecord, 'audioLevel');

        setAudioMetric((prev) => ({
          jitterMs: jitterSeconds != null ? jitterSeconds * 1000 : prev.jitterMs,
          packetLossRatio: lossRatio ?? prev.packetLossRatio,
          audioLevel: audioLevel ?? prev.audioLevel,
        }));
        setNetworkMetric((prev) => ({
          rttMs: prev.rttMs,
          bitrateKbps: prev.bitrateKbps,
          packetLossRatio: lossRatio ?? prev.packetLossRatio,
        }));
      }
    };

    currentCall.on('webRTCStat', handleStat);
    return () => {
      currentCall.off('webRTCStat', handleStat);
    };
  }, [currentCall, callState]);

  useEffect(() => {
    if (!selectedInputId) {
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return;
    }

    let cancelled = false;

    const applyInput = async () => {
      const existingTrack = activeInputStreamRef.current?.getAudioTracks?.()[0];
      const existingDeviceId = existingTrack?.getSettings?.().deviceId;

      let stream = activeInputStreamRef.current;

      if (!stream || existingDeviceId !== selectedInputId) {
        try {
          const nextStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: selectedInputId },
            },
          });

          if (cancelled) {
            nextStream.getTracks().forEach((track) => track.stop());
            return;
          }

          if (activeInputStreamRef.current && activeInputStreamRef.current !== nextStream) {
            activeInputStreamRef.current.getTracks().forEach((track) => track.stop());
          }
          activeInputStreamRef.current = nextStream;
          stream = nextStream;
        } catch (error) {
          if (!cancelled) {
            console.error('Falha ao acessar microfone selecionado', error);
            setAudioDeviceError('Nao foi possivel acessar o microfone selecionado.');
          }
          return;
        }
      }

      if (!stream) {
        return;
      }

      const track = stream.getAudioTracks()[0];
      if (!track) {
        return;
      }

      try {
        const setAudioInputDevice = getRecordFunction<[string], void | Promise<void>>(operator, 'setAudioInputDevice');
        if (setAudioInputDevice) {
          await Promise.resolve(setAudioInputDevice(selectedInputId));
          setAudioDeviceError(null);
          return;
        }

        const connection = extractPeerConnection(currentCall);
        const sender = connection?.getSenders?.().find((item) => item.track?.kind === 'audio');

        if (sender?.replaceTrack) {
          await sender.replaceTrack(track);
          setAudioDeviceError(null);
          return;
        }

        const setInputTrack = getRecordFunction<[MediaStreamTrack], void | Promise<void>>(currentCall, 'setInputTrack');
        if (setInputTrack) {
          await Promise.resolve(setInputTrack(track));
          setAudioDeviceError(null);
          return;
        }

        const replaceTrack = getRecordFunction<[MediaStreamTrack], void | Promise<void>>(currentCall, 'replaceTrack');
        if (replaceTrack) {
          await Promise.resolve(replaceTrack(track));
          setAudioDeviceError(null);
          return;
        }

        console.warn('Nao foi possivel aplicar o microfone via API conhecida');
        setAudioDeviceError(null);
      } catch (error) {
        if (!cancelled) {
          console.error('Erro ao aplicar microfone', error);
          setAudioDeviceError('Nao foi possivel aplicar o microfone selecionado.');
        }
      }
    };

    void applyInput();

    return () => {
      cancelled = true;
    };
  }, [selectedInputId, operator, currentCall]);

  useEffect(() => {
    if (!selectedOutputId || !operator) {
      return;
    }

    const applyOutput = async () => {
      try {
        const audioOutput = getRecordValue<unknown>(operator, 'audioOutput');
        const setAudioOutputDevice = getRecordFunction<[string], void | Promise<void>>(audioOutput, 'setAudioOutputDevice');

        if (!setAudioOutputDevice) {
          console.warn('Nao foi possivel localizar API para alterar dispositivo de saida.');
          return;
        }

        await Promise.resolve(setAudioOutputDevice(selectedOutputId));
        setAudioDeviceError(null);
      } catch (error) {
        console.error('Erro ao aplicar dispositivo de saida', error);
        setAudioDeviceError('Nao foi possivel aplicar a saida de audio selecionada.');
      }
    };

    void applyOutput();
  }, [selectedOutputId, operator]);

  useEffect(() => {
    if (!currentCall) {
      return;
    }

    const handleFailed = (event: { failure?: { cause?: string; message?: string } }) => {
      const cause = event?.failure?.message || event?.failure?.cause;
      setCallError(cause ? `Chamada nao concluida: ${cause}` : 'Chamada nao concluida.');
    };

    currentCall.on('failed', handleFailed);
    return () => {
      currentCall.off('failed', handleFailed);
    };
  }, [currentCall]);

  useEffect(() => {
    return () => {
      const ctx = toneContextRef.current;
      if (ctx) {
        ctx.close().catch(() => undefined);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      activeInputStreamRef.current?.getTracks().forEach((track) => track.stop());
      activeInputStreamRef.current = null;
    };
  }, []);


  const ensureAudioContext = useCallback(async () => {
    if (typeof window === 'undefined') {
      return null;
    }

    const ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!ctor) {
      return null;
    }

    if (!toneContextRef.current) {
      toneContextRef.current = new ctor();
    }

    if (toneContextRef.current.state === 'suspended') {
      try {
        await toneContextRef.current.resume();
      } catch (error) {
        console.error('Falha ao retomar audio', error);
      }
    }

    return toneContextRef.current;
  }, []);

  const playTone = useCallback(
    async (digit: string) => {
      const freqs = DTMF_FREQUENCIES[digit as keyof typeof DTMF_FREQUENCIES];
      if (!freqs) {
        return;
      }

      const ctx = await ensureAudioContext();
      if (!ctx) {
        return;
      }

      const gain = ctx.createGain();
      const [f1, f2] = freqs;
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();

      osc1.type = 'sine';
      osc2.type = 'sine';
      osc1.frequency.setValueAtTime(f1, ctx.currentTime);
      osc2.frequency.setValueAtTime(f2, ctx.currentTime);

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + 0.01);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.18);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      const now = ctx.currentTime;
      const stopTime = now + 0.2;
      osc1.start(now);
      osc2.start(now);
      osc1.stop(stopTime);
      osc2.stop(stopTime);

      const cleanup = () => {
        try {
          osc1.disconnect();
          osc2.disconnect();
          gain.disconnect();
        } catch (error) {
          console.error('Erro ao limpar tons DTMF', error);
        }
      };

      osc1.onended = cleanup;
      osc2.onended = cleanup;
    },
    [ensureAudioContext]
  );

  const handleDial = useCallback(
    (digit: string) => {
      void playTone(digit);

      if (currentCall && (isInCall || isRinging)) {
        try {
          currentCall.sendDtmf(digit);
        } catch (error) {
          console.error('Falha ao enviar DTMF', error);
          setCallError('Nao foi possivel enviar o tom DTMF.');
        }
        return;
      }

      if (!canEditNumber) {
        return;
      }

      setCallError(null);

      // Validar que é um dígito válido (0-9, *, #)
      if (!/^[0-9*#]$/.test(digit)) {
        console.warn('[handleDial] Dígito inválido ignorado:', digit);
        return;
      }

      // Capturar posição do cursor AGORA (antes do setState)
      const capturedPos = cursorPosition;

      // Usar função no setState para garantir que pegamos o valor mais recente
      setDialValue((prevValue: string) => {
        // Obter o valor atual do input (já sanitizado)
        const currentValue = prevValue;

        // Inserir dígito na posição do cursor capturada
        const newValue = currentValue.slice(0, capturedPos) + digit + currentValue.slice(capturedPos);

        // Limitar ao tamanho máximo
        const trimmedValue = newValue.slice(0, MAX_DIAL_LENGTH);

        // Atualizar posição do cursor após inserção
        const newCursorPos = Math.min(capturedPos + 1, trimmedValue.length);
        setCursorPosition(newCursorPos);

        setTimeout(() => {
          if (visibleInputRef.current) {
            visibleInputRef.current.focus();
            visibleInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          }
        }, 0);

        return trimmedValue;
      });
    },
    [playTone, currentCall, isInCall, isRinging, canEditNumber, cursorPosition, setDialValue]
  );

  const handleClear = useCallback(() => {
    setCallError(null);
    setDialValue('');
    setCursorPosition(0);

    // Focar no input após limpar
    setTimeout(() => {
      if (visibleInputRef.current) {
        visibleInputRef.current.focus();
      }
    }, 0);
  }, [setDialValue]);

  const handleBackspace = useCallback(() => {
    setCallError(null);

    // Capturar a posição do cursor AGORA
    const capturedPos = cursorPosition;

    // Se cursor está no início, não faz nada
    if (capturedPos === 0) {
      return;
    }

    // Usar função no setState para garantir que pegamos o valor mais recente
    setDialValue((prevValue: string) => {
      // Remove o caractere antes do cursor
      const newValue = prevValue.slice(0, capturedPos - 1) + prevValue.slice(capturedPos);

      // Atualizar posição do cursor após remoção
      const newCursorPos = capturedPos - 1;
      setCursorPosition(newCursorPos);

      setTimeout(() => {
        if (visibleInputRef.current) {
          visibleInputRef.current.focus();
          visibleInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);

      return newValue;
    });
  }, [setDialValue, cursorPosition]);

  const handleCall = useCallback(async () => {
    // Allow calls when idle OR on break
    if (!isIdle && !isOnBreak) {
      return;
    }

    if (!sanitizedDialValue) {
      setCallError('Informe um numero para ligar.');
      return;
    }

    // Normalizar o número (adiciona 9 se necessário, remove prefixos, etc)
    const normalizedLocal = normalizePhoneNumberPaste(sanitizedDialValue);
    console.log('[handleCall] sanitizedDialValue:', sanitizedDialValue);
    console.log('[handleCall] normalizedLocal após normalizePhoneNumberPaste:', normalizedLocal);

    // Converter para E.164 (+55...)
    const e164Number = normalizeToE164(normalizedLocal, DEFAULT_COUNTRY_PREFIX);
    console.log('[handleCall] e164Number após normalizeToE164:', e164Number);

    if (!e164Number) {
      setCallError('Informe um numero para ligar.');
      return;
    }

    // Validar usando libphonenumber
    const isValid = isValidPhoneNumberSafe(e164Number);
    console.log('[handleCall] isValidPhoneNumberSafe:', isValid);

    if (!isValid) {
      setCallError('Numero invalido. Digite um numero de celular valido com DDD.');
      return;
    }

    console.log('[handleCall] 🚀 Ligando para:', e164Number);

    try {
      setCallError(null);
      setLastDialedNumber(e164Number);
      if (makeManualCall) {
        void makeManualCall(e164Number);
      }
    } catch (error) {
      console.error('Erro ao iniciar chamada', error);
      setCallError('Nao foi possivel iniciar a ligacao.');
    }
  }, [isIdle, isOnBreak, sanitizedDialValue, makeManualCall]);

  const handleGoOnBreak = useCallback(async (breakId: number) => {
    if (!goOnBreak) return;
    
    try {
      setBreakError(null);
      await goOnBreak(breakId);
      setIsBreakModalOpen(false);
      console.log('[FloatingDialer] Entrou em pausa com sucesso:', breakId);
    } catch (error) {
      console.error('[FloatingDialer] Erro ao entrar em pausa:', error);
      setBreakError(error instanceof Error ? error.message : 'N�o foi poss�vel entrar em pausa.');
    }
  }, [goOnBreak]);

  const handleGoOffBreak = useCallback(async () => {
    if (!goOffBreak) return;
    
    try {
      await goOffBreak();
      console.log('[FloatingDialer] Saiu da pausa com sucesso');
    } catch (error) {
      console.error('[FloatingDialer] Erro ao sair da pausa:', error);
      setAvailabilityError('N�o foi poss�vel sair da pausa.');
    }
  }, [goOffBreak]);

  // Sincronizar o cursor quando o dialValue muda (útil ao colar ou limpar)
  useEffect(() => {
    if (visibleInputRef.current && sanitizedDialValue.length < cursorPosition) {
      // Se o valor ficou menor que a posição do cursor, ajustar
      const newPos = sanitizedDialValue.length;
      setCursorPosition(newPos);
      visibleInputRef.current.setSelectionRange(newPos, newPos);
    }
  }, [sanitizedDialValue, cursorPosition]);

  useEffect(() => {
    // No modo mini, o input tem seu próprio onKeyDown - não usar listener global
    if (isMiniMode) {
      return;
    }

    // No modo full, só quando focado
    if (!isDialerFocused) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const key = event.key;

      if (key === 'Backspace') {
        event.preventDefault();
        handleBackspace();
        return;
      }

      if (key === 'Delete') {
        event.preventDefault();
        handleClear();
        return;
      }

      if (key === 'Enter') {
        if (isIdle) {
          event.preventDefault();
          void handleCall();
        }
        return;
      }

      const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '#', '*', '+'];
      const mappedKey = key === 'NumpadEnter' ? null : key;

      if (mappedKey && allowedKeys.includes(mappedKey)) {
        event.preventDefault();
        handleDial(mappedKey);
        return;
      }

      if (key.startsWith('Numpad')) {
        const digit = key.replace('Numpad', '');
        if (digit === 'Add') {
          event.preventDefault();
          handleDial('+');
          return;
        }
        if (digit === 'Multiply') {
          event.preventDefault();
          handleDial('*');
          return;
        }
        if (/^[0-9]$/.test(digit)) {
          event.preventDefault();
          handleDial(digit);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMiniMode, isDialerFocused, handleBackspace, handleClear, handleDial, handleCall, isIdle]);

  const handleHangup = useCallback(async () => {
    if (!hangup) return;
    
    try {
      await hangup();
    } catch (error) {
      console.error('Erro ao encerrar chamada', error);
      setCallError('Falha ao encerrar chamada.');
    }
  }, [hangup]);

  const handleAnswer = useCallback(async () => {
    if (!answer) return;
    
    try {
      await answer();
    } catch (error) {
      console.error('Erro ao atender chamada', error);
      setCallError('Nao foi possivel atender.');
    }
  }, [answer]);

  const handleReject = useCallback(async () => {
    if (!reject) return;
    
    try {
      await reject();
    } catch (error) {
      console.error('Erro ao rejeitar chamada', error);
      setCallError('Nao foi possivel rejeitar.');
    }
  }, [reject]);

  const handleFinishAfterCall = useCallback(async () => {
    const qualifications = afterCallResult === 'success' ? successQualifications : discardQualifications;
    if (!qualifications.length) {
      setAfterCallError('Nenhuma qualificacao configurada para este resultado.');
      return;
    }

    if (!selectedQualificationId) {
      setAfterCallError('Selecione uma qualificacao para finalizar.');
      return;
    }

    if (typeof finishAfterCall !== 'function') {
      setAfterCallError('Nao foi possivel enviar a qualificacao.');
      return;
    }

    try {
      setIsSubmittingAfterCall(true);
      setAfterCallError(null);
      await Promise.resolve(
        finishAfterCall({
          result: afterCallResult,
          qualificationId: selectedQualificationId,
        })
      );
    } catch (error) {
      console.error('Erro ao finalizar pos-chamada', error);
      setAfterCallError('Falha ao finalizar atendimento. Tente novamente.');
    } finally {
      setIsSubmittingAfterCall(false);
    }
  }, [afterCallResult, discardQualifications, finishAfterCall, selectedQualificationId, successQualifications]);

  const handleFocusWithin = useCallback(() => {
    setIsDialerFocused(true);
  }, []);

  const handleBlurWithin = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (!dialerCardRef.current || !next || !dialerCardRef.current.contains(next)) {
      setIsDialerFocused(false);
    }
  }, []);

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    if (!pastedText) {
      return;
    }

    // Normaliza o número colado usando a função utilitária (adiciona 9 se necessário)
    const normalized = normalizePhoneNumberPaste(pastedText);

    // Atualiza o valor do dialer com o número normalizado
    setDialValue(normalized);

    // Posiciona o cursor no final do número colado
    setCursorPosition(normalized.length);
    setTimeout(() => {
      if (visibleInputRef.current) {
        visibleInputRef.current.focus();
        visibleInputRef.current.setSelectionRange(normalized.length, normalized.length);
      }
    }, 0);
  }, [setDialValue]);

  const handleSelectInputDevice = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedInputId(value || null);
  }, []);

  const handleSelectOutputDevice = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedOutputId(value || null);
  }, []);

  const setVolumeClamped = useCallback(
    (value: number) => {
      const safe = Number.isFinite(value) ? value : 0;
      const next = Math.min(Math.max(safe, 0), 1);
      const currentVolume = typeof outputVolume === 'number' ? outputVolume : 0;
      const scale = currentVolume > 1 ? 100 : 1;
      setOutputVolume(Number((next * scale).toFixed(2)));
    },
    [outputVolume, setOutputVolume]
  );

  const operatorCall = operatorState && 'call' in operatorState ? operatorState.call : undefined;
  const callCounterpart = extractCounterpart(currentCall) ?? extractCounterpart(operatorCall);
  const currentNumber = normalizedNumber || lastDialedNumber;
  
  // Melhor l�gica para obter o n�mero da chamada com m�ltiplos fallbacks
  const callTarget = 
    callCounterpart?.displayName ?? 
    callCounterpart?.phoneNumber ?? 
    (currentCallInfo?.type === 'campaign' ? currentCallInfo.info.campaignContact.phoneNumber : undefined) ??
    ((isInCall || isConnecting) && currentNumber ? currentNumber : undefined);
    
  const statusLabel = getStatusLabel(callState);
  const dialDisplayText = formattedHintNumber || sanitizedDialValue;
  const inputDeviceLabel =
    audioDevices.inputs.find((device) => device.deviceId === selectedInputId)?.label || 'Microfone padrao';
  const outputDeviceLabel =
    audioDevices.outputs.find((device) => device.deviceId === selectedOutputId)?.label || 'Saida padrao';
  const rawVolume = typeof outputVolume === 'number' ? outputVolume : 0;
  const volumeScale = rawVolume > 1 ? 100 : 1;
  const normalizedVolume = Math.min(Math.max(rawVolume / volumeScale, 0), 1);
  const volumePercent = Math.round(normalizedVolume * 100);
  const SpeakerIcon = outputMuted ? FiVolumeX : normalizedVolume > 0.65 ? FiVolume2 : FiVolume1;
  const MicIcon = inputMuted ? FiMicOff : FiMic;
  const statusVisual = getStatusVisual(callState);

  // MODO FLOATING: AfterCall usa o painel completo com header "LOGADO COMO"
  if (callState === 'afterCall' && !isMiniMode) {
    return (
      <AfterCallPanel
        username={username}
        result={afterCallResult}
        onChangeResult={setAfterCallResult}
        successQualifications={successQualifications}
        discardQualifications={discardQualifications}
        selectedQualificationId={selectedQualificationId}
        onSelectQualification={setSelectedQualificationId}
        onSubmit={handleFinishAfterCall}
        isSubmitting={isSubmittingAfterCall}
        errorMessage={afterCallError}
        onLogout={onLogout}
        isMobile={isMobileLayout}
        onClose={onRequestClose}
      />
    );
  }

  const modalOverlayClass = portalTarget
    ? tw`absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-4`
    : tw`fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4`;

  const breakModalContent = (
    <div className={modalOverlayClass}>
      <div className={tw`bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4`}>
        <div className={tw`flex items-center justify-between`}>
          <h3 className={tw`text-lg font-bold text-slate-800`}>Selecione uma Pausa</h3>
          <button
            type="button"
            onClick={() => {
              setIsBreakModalOpen(false);
              setBreakError(null);
            }}
            className={tw`text-slate-400 hover:text-slate-600 transition`}
          >
            <FiX className={tw`text-xl`} />
          </button>
        </div>

        {breakError && (
          <div className={tw`rounded-lg bg-red-50 border border-red-200 px-3 py-2 flex items-start gap-2`}>
            <FiAlertTriangle className={tw`text-red-500 shrink-0 mt-0.5`} />
            <p className={tw`text-sm text-red-700`}>{breakError}</p>
          </div>
        )}

        {!breaks || breaks.length === 0 ? (
          <div className={tw`text-center py-8`}>
            <p className={tw`text-slate-500`}>Nenhuma pausa disponivel</p>
          </div>
        ) : (
          <div className={tw`space-y-2 max-h-96 overflow-y-auto`}>
            {breaks.map((breakItem) => (
              <button
                key={breakItem.id}
                type="button"
                onClick={() => handleGoOnBreak(breakItem.id)}
                className={tw`w-full flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition-all hover:border-amber-300 hover:bg-amber-50 hover:shadow-md`}
              >
                <FiPauseCircle className={tw`text-xl text-amber-600 shrink-0`} />
                <div className={tw`flex-1 min-w-0`}>
                  <p className={tw`font-semibold text-slate-800 truncate`}>{breakItem.name}</p>
                  <p className={tw`text-xs text-slate-500`}>ID: {breakItem.id}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            setIsBreakModalOpen(false);
            setBreakError(null);
          }}
          className={tw`w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100`}
        >
          Cancelar
        </button>
      </div>
    </div>
  );

  const breakModal = isBreakModalOpen
    ? portalTarget
      ? createPortal(breakModalContent, portalTarget)
      : breakModalContent
    : null;

  const showActiveSummary = isInCall || isConnecting;

  if (isMobileLayout && !isMiniMode) {
    const mobileCanUseKeypad = !availabilityError && (canUseKeypad || isIdle || isOnBreak);
    const displayNumber = dialDisplayText || sanitizedDialValue || 'Digite ou cole um numero';

    return (
      <div
        ref={dialerCardRef}
        tabIndex={0}
        role="application"
        aria-label="Discador mobile"
        onFocusCapture={handleFocusWithin}
        onBlurCapture={handleBlurWithin}
        className={tw`callix mobile flex min-h-[calc(100vh-3rem)] w-full max-w-full flex-col gap-4 rounded-3xl border border-slate-200 bg-gradient-to-b from-white via-slate-50 to-white p-4 shadow-xl outline-none`}
      >
        <div className={tw`flex items-center justify-between gap-2`}>
          <button
            type="button"
            onClick={() => {
              if (onRequestClose) {
                onRequestClose();
              } else {
                handleClear();
              }
            }}
            className={tw`inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:shadow-md`}
          >
            <FiArrowLeft className={tw`text-base`} />
            Voltar
          </button>

          <div
            className={tw`${statusVisual.badgeClass} inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold`}
          >
            <statusVisual.Icon className={tw`text-xs`} />
            <span>{statusLabel}</span>
          </div>

          <button
            type="button"
            onClick={isOnBreak ? handleGoOffBreak : () => setIsBreakModalOpen(true)}
            className={tw`inline-flex items-center gap-1.5 rounded-full border ${
              isOnBreak ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-700'
            } px-3 py-2 text-sm font-semibold transition hover:shadow-md`}
          >
            {isOnBreak ? <FiCheckCircle className={tw`text-sm`} /> : <FiPauseCircle className={tw`text-sm`} />}
            {isOnBreak ? 'Voltar' : 'Pausa'}
          </button>
        </div>

        {showActiveSummary ? (
          <div className={tw`space-y-3`}>
            <ActiveCallSummary statusLabel={statusLabel} callTarget={callTarget} durationSeconds={callDurationSeconds} />
            <CallInfoDisplay />
          </div>
        ) : (
          <div className={tw`space-y-2 rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-inner`}>
            <div className={tw`flex items-center justify-between`}>
              <span className={tw`text-[11px] font-semibold uppercase tracking-wide text-slate-500`}>Número</span>
              {formattedHintNumber && (
                <span className={tw`text-[11px] font-semibold text-primary`}>{formattedHintNumber}</span>
              )}
            </div>
            <div className={tw`flex items-center gap-3`}>
              <input
                ref={visibleInputRef}
                type="text"
                inputMode="tel"
                value={sanitizedDialValue}
                onChange={(e) => {
                  const input = e.target as HTMLInputElement;
                  setCursorPosition(input.selectionStart || 0);
                }}
                onClick={(e) => setCursorPosition((e.target as HTMLInputElement).selectionStart || 0)}
                onKeyUp={(e) => setCursorPosition((e.target as HTMLInputElement).selectionStart || 0)}
                onSelect={(e) => setCursorPosition((e.target as HTMLInputElement).selectionStart || 0)}
                onKeyDown={(e) => {
                  if (!mobileCanUseKeypad) {
                    e.preventDefault();
                    return;
                  }

                  const key = e.key;
                  const input = e.target as HTMLInputElement;
                  const currentPos = input.selectionStart || 0;
                  setCursorPosition(currentPos);

                  if (key === 'Backspace') {
                    e.preventDefault();
                    handleBackspace();
                    return;
                  }

                  if (key === 'Delete') {
                    e.preventDefault();
                    const capturedPos = currentPos;
                    setDialValue((prevValue: string) => {
                      if (capturedPos < prevValue.length) {
                        const newValue = prevValue.slice(0, capturedPos) + prevValue.slice(capturedPos + 1);
                        setTimeout(() => {
                          if (visibleInputRef.current) {
                            visibleInputRef.current.setSelectionRange(capturedPos, capturedPos);
                            setCursorPosition(capturedPos);
                          }
                        }, 0);
                        return newValue;
                      }
                      return prevValue;
                    });
                    return;
                  }

                  if (key === 'Enter') {
                    e.preventDefault();
                    void handleCall();
                    return;
                  }

                  if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
                    return;
                  }

                  if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(key.toLowerCase())) {
                    return;
                  }

                  if (/^[0-9*#]$/.test(key)) {
                    e.preventDefault();
                    handleDial(key);
                    return;
                  }

                  e.preventDefault();
                }}
                onPaste={handlePaste}
                disabled={!mobileCanUseKeypad}
                placeholder="Digite ou cole um numero"
                className={tw`flex-1 bg-transparent text-3xl font-bold text-slate-900 outline-none placeholder:text-slate-300 disabled:opacity-60`}
              />
              <div className={tw`flex items-center gap-2`}>
                <button
                  type="button"
                  onClick={handleBackspace}
                  disabled={!mobileCanUseKeypad}
                  className={tw`inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:shadow-md disabled:opacity-40`}
                  aria-label="Apagar último digito"
                >
                  <FiDelete className={tw`text-lg`} />
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={!mobileCanUseKeypad}
                  className={tw`inline-flex h-10 w-10 items-center justify-center rounded-full border border-rose-300 bg-rose-50 text-rose-600 shadow-sm transition hover:border-rose-400 hover:bg-rose-100 hover:shadow-md disabled:opacity-40`}
                  aria-label="Limpar número"
                >
                  <FiX className={tw`text-lg`} />
                </button>
              </div>
            </div>
            <p className={tw`text-[11px] font-medium text-slate-500`}>
              Formatação automática: {displayNumber}
            </p>
          </div>
        )}

        <div className={tw`grid grid-cols-3 gap-2`}>
          {keypadKeys.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => handleDial(item.value)}
              disabled={!mobileCanUseKeypad}
              className={tw`flex h-14 flex-col items-center justify-center rounded-xl border border-slate-200 bg-white text-xl font-bold text-slate-900 shadow-sm transition active:scale-95 hover:border-slate-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <span>{item.value}</span>
              {item.detail && (
                <span className={tw`text-[9px] font-semibold uppercase tracking-widest text-slate-400`}>
                  {item.detail}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className={tw`grid grid-cols-3 gap-2`}>
          <button
            type="button"
            onClick={() => setInputMuted(!inputMuted)}
            className={tw`${inputMuted ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-slate-700'} flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition hover:border-slate-300 hover:shadow-md`}
          >
            <MicIcon className={tw`text-base`} />
            {inputMuted ? 'Mudo' : 'Áudio'}
          </button>
          <button
            type="button"
            onClick={() => setOutputMuted(!outputMuted)}
            className={tw`${outputMuted ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-700'} flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition hover:border-slate-300 hover:shadow-md`}
          >
            <SpeakerIcon className={tw`text-base`} />
            {outputMuted ? 'Saída off' : `${volumePercent}%`}
          </button>
          <button
            type="button"
            onClick={isOnBreak ? handleGoOffBreak : () => setIsBreakModalOpen(true)}
            className={tw`${isOnBreak ? 'border-green-300 bg-green-50 text-green-700' : 'border-amber-300 bg-amber-50 text-amber-700'} flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition hover:shadow-md`}
          >
            {isOnBreak ? <FiCheckCircle className={tw`text-base`} /> : <FiPauseCircle className={tw`text-base`} />}
            {isOnBreak ? 'Voltar' : 'Pausa'}
          </button>
        </div>

        <div className={tw`flex flex-wrap items-center gap-2`}>
          {(isIdle || isOnBreak) ? (
            <button
              type="button"
              onClick={handleCall}
              disabled={!isNumberValid}
              className={tw`flex flex-1 min-w-[8rem] items-center justify-center gap-2 rounded-xl bg-green-500 px-4 py-3 text-base font-bold text-white shadow-lg transition hover:bg-green-600 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60`}
            >
              <FiPhone className={tw`text-lg`} />
              Ligar
            </button>
          ) : isIncoming ? (
            <>
              <button
                type="button"
                onClick={handleReject}
                className={tw`flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-rose-600`}
              >
                <FiPhoneOff className={tw`text-base`} />
                Recusar
              </button>
              <button
                type="button"
                onClick={handleAnswer}
                className={tw`flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-xl bg-green-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-green-600`}
              >
                <FiPhoneIncoming className={tw`text-base`} />
                Atender
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleHangup}
              className={tw`flex flex-1 min-w-[8rem] items-center justify-center gap-2 rounded-xl bg-rose-500 px-4 py-3 text-base font-bold text-white shadow-lg transition hover:bg-rose-600 hover:shadow-xl`}
            >
              <FiPhoneOff className={tw`text-lg`} />
              Encerrar
            </button>
          )}

          <button
            type="button"
            onClick={onLogout}
            className={tw`inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-red-400 hover:text-red-500`}
          >
            <FiLogOut className={tw`text-base`} />
            Sair
          </button>
        </div>

        {(callError || availabilityError || audioDeviceError) && (
          <div className={tw`space-y-1.5`}>
            {callError && (
              <div className={tw`flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-2.5 py-2 text-[11px] text-rose-700`}>
                <FiAlertTriangle className={tw`mt-0.5 text-xs`} />
                <span>{callError}</span>
              </div>
            )}
            {availabilityError && (
              <div className={tw`flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-700`}>
                <FiAlertTriangle className={tw`mt-0.5 text-xs`} />
                <span>{availabilityError}</span>
              </div>
            )}
            {audioDeviceError && (
              <div className={tw`flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-2.5 py-2 text-[11px] text-sky-700`}>
                <FiAlertTriangle className={tw`mt-0.5 text-xs`} />
                <span>{audioDeviceError}</span>
              </div>
            )}
          </div>
        )}

        {breakModal}
      </div>
    );
  }

  if (isMiniMode) {
    const isCallActive = isInCall || isConnecting;
    // No modo mini, permitir ligar se houver qualquer n�mero digitado (n�o precisa ser v�lido)
    const hasNumber = sanitizedDialValue.length > 0;
    const callButtonDisabled = !isCallActive && !hasNumber;
    // No modo mini, sempre permitir teclado quando n�o houver erro de disponibilidade
    const miniCanUseKeypad = !availabilityError && (canUseKeypad || isIdle || isOnBreak);
    
    // Debug: Log estado do teclado e bot�o
    console.log('[FloatingDialer Mini] Estado:', {
      miniCanUseKeypad,
      callButtonDisabled,
      hasNumber,
      dialValue: sanitizedDialValue,
      isNumberValid,
      availabilityError,
      callState,
      operatorState: operatorState?.state
    });

    // ======= TELA DE QUALIFICAÇÃO (AFTERCALL) =======
    if (callState === 'afterCall') {
      const options = afterCallResult === 'success' ? successQualifications : discardQualifications;
      const hasOptions = options.length > 0;

      console.log('[FloatingDialer Mini] ?? TELA DE QUALIFICA��O:', {
        callState,
        afterCallResult,
        successQualifications: successQualifications.length,
        discardQualifications: discardQualifications.length,
        hasOptions,
        selectedQualificationId
      });

      return (
        <div
          className={['callix', 'mode', 'min', tw`flex w-full max-w-full flex-col gap-1 rounded-xl border border-slate-200 bg-white/95 p-1.5 shadow-sm text-slate-800 overflow-hidden`].join(' ')}
          data-callix-variant="min"
        >
          <div className={tw`space-y-1 rounded-lg border border-slate-200 bg-slate-50 px-1.5 py-1 overflow-hidden`}>
            <div className={tw`text-center`}>
              <p className={tw`text-[9px] font-semibold text-slate-700`}>Qualifique a chamada</p>
            </div>

            <div className={tw`grid grid-cols-2 gap-0.5`}>
              <button
                type="button"
                onClick={() => setAfterCallResult('success')}
                className={tw`${afterCallResult === 'success' ? 'bg-green-500 text-white' : 'bg-white text-slate-600'} rounded border border-slate-200 px-1 py-0.5 text-[7px] font-semibold transition`}
              >
                ✓ Concluído
              </button>
              <button
                type="button"
                onClick={() => setAfterCallResult('failure')}
                className={tw`${afterCallResult === 'failure' ? 'bg-rose-500 text-white' : 'bg-white text-slate-600'} rounded border border-slate-200 px-1 py-0.5 text-[7px] font-semibold transition`}
              >
                ✗ Descartado
              </button>
            </div>

            <div className={tw`space-y-0.5`}>
              <p className={tw`text-[7px] font-medium text-slate-700 text-center`}>Selecione qualificação</p>
              {hasOptions ? (
                <div className={tw`max-h-16 space-y-0.5 overflow-y-auto`}>
                  {options.map((qualification) => {
                    const isSelected = selectedQualificationId === qualification.id;
                    return (
                      <button
                        key={qualification.id}
                        type="button"
                        onClick={() => setSelectedQualificationId(qualification.id)}
                        className={tw`${isSelected ? 'bg-blue-100 border-blue-300 text-blue-800' : 'border-slate-200 bg-white text-slate-700'} w-full rounded border px-1 py-0.5 text-left transition`}
                      >
                        <p className={tw`text-[7px] font-medium leading-tight truncate`}>{qualification.name}</p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className={tw`rounded border border-amber-200 bg-amber-50 px-1 py-0.5 text-[7px] text-amber-700 text-center`}>
                  Nenhuma qualificação
                </div>
              )}
            </div>

            {afterCallError && (
              <p className={tw`text-[7px] text-red-500 text-center`}>{afterCallError}</p>
            )}

            <button
              type="button"
              onClick={handleFinishAfterCall}
              disabled={!hasOptions || !selectedQualificationId || isSubmittingAfterCall}
              className={tw`flex w-full items-center justify-center gap-1 rounded bg-blue-500 px-1.5 py-1 text-[7px] font-bold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {isSubmittingAfterCall && (
                <span className={tw`inline-flex h-2 w-2 animate-spin rounded-full border border-white/50 border-t-white`} />
              )}
              Finalizar
            </button>
          </div>
        </div>
      );
    }

    // ======= TECLADO NORMAL (IDLE/CHAMADA ATIVA) =======
    console.log('[FloatingDialer Mini] ?? Estado da Chamada:', {
      isCallActive,
      callState,
      statusLabel,
      isInCall,
      isConnecting,
      isRinging,
      callDurationSeconds,
      callTarget,
      statusVisualUsed: isCallActive ? 'GREEN (green)' : 'statusVisual',
      timerVisible: isCallActive && callDurationSeconds > 0
    });

    return (
      <div
        className={['callix', 'mode', 'min', tw`flex w-full max-w-full flex-col gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm text-slate-800`].join(' ')}
        data-callix-variant="min"
      >
        {/* Input oculto para capturar paste */}
        <input
          ref={hiddenInputRef}
          type="tel"
          inputMode="tel"
          value={sanitizedDialValue}
          onChange={(event) => setDialValue(event.target.value)}
          onPaste={handlePaste}
          className={tw`sr-only`}
          tabIndex={-1}
          aria-hidden="true"
        />
        <div className={tw`flex items-center justify-between gap-2`}>
          <div className={tw`${isCallActive ? 'border-green-200 bg-green-50 text-green-700' : statusVisual.badgeClass} flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight`}>
            <statusVisual.Icon className={tw`text-xs`} />
            <span>{statusLabel}</span>
          </div>
          {isOnBreak ? (
            <button
              type="button"
              onClick={() => handleGoOffBreak()}
              className={tw`inline-flex items-center gap-1 rounded-full border border-green-400 bg-green-50 px-2.5 py-1 text-[10px] font-semibold text-green-700 transition hover:bg-green-100`}
            >
              <FiCheckCircle className={tw`text-xs`} />
              Voltar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsBreakModalOpen(true)}
              className={tw`inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700 transition hover:bg-amber-100`}
            >
              <FiPauseCircle className={tw`text-xs`} />
              Pausa
            </button>
          )}
        </div>

        {/* VISOR: Mostra n�mero durante digita��o OU informa��es da chamada quando ativa */}
        {isCallActive ? (
          <div className={tw`rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-white px-3 py-2`}>
            <div className={tw`flex items-center gap-2`}>
              <div className={tw`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-500 ${callDurationSeconds > 0 ? 'animate-pulse' : ''}`}>
                <FiPhoneCall className={tw`text-xs text-white`} />
              </div>
              <div className={tw`flex min-w-0 flex-1 flex-col gap-0.5`}>
                <span className={tw`truncate font-mono text-sm font-bold text-slate-800`}>
                  {callTarget?.replace(/^\+55/, '') || 'Conectando...'}
                </span>
                <span className={tw`text-[9px] font-medium uppercase tracking-wider text-green-600`}>
                  {statusLabel}
                </span>
              </div>
              {callDurationSeconds > 0 && (
                <div className={tw`flex shrink-0 items-center gap-1 rounded-lg bg-green-500 px-2 py-1`}>
                  <FiClock className={tw`text-[10px] text-white`} />
                  <span className={tw`tabular-nums text-xs font-bold text-white`}>
                    {formatDuration(callDurationSeconds)}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={tw`space-y-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2`}
          >
            <span className={tw`text-[10px] font-semibold uppercase tracking-wide text-slate-500`}>Numero</span>
            <div className={tw`flex items-center gap-2`}>
              <input
                ref={visibleInputRef}
                type="text"
                inputMode="tel"
                value={sanitizedDialValue}
                onChange={(e) => {
                  // NÃO fazer nada aqui - o onKeyDown já trata tudo
                  // Apenas atualizar a posição do cursor
                  const input = e.target as HTMLInputElement;
                  setCursorPosition(input.selectionStart || 0);
                }}
                onClick={(e) => {
                  setCursorPosition((e.target as HTMLInputElement).selectionStart || 0);
                }}
                onKeyUp={(e) => {
                  setCursorPosition((e.target as HTMLInputElement).selectionStart || 0);
                }}
                onSelect={(e) => {
                  setCursorPosition((e.target as HTMLInputElement).selectionStart || 0);
                }}
                onKeyDown={(e) => {
                  if (!miniCanUseKeypad) {
                    e.preventDefault();
                    return;
                  }

                  const key = e.key;
                  const input = e.target as HTMLInputElement;
                  const currentPos = input.selectionStart || 0;

                  // Atualizar posição do cursor
                  setCursorPosition(currentPos);

                  // AÇÃO: Backspace ou Delete
                  if (key === 'Backspace') {
                    e.preventDefault();
                    handleBackspace();
                    return;
                  }

                  if (key === 'Delete') {
                    e.preventDefault();
                    const capturedPos = currentPos;

                    setDialValue((prevValue: string) => {
                      if (capturedPos < prevValue.length) {
                        const newValue = prevValue.slice(0, capturedPos) + prevValue.slice(capturedPos + 1);

                        setTimeout(() => {
                          if (visibleInputRef.current) {
                            visibleInputRef.current.setSelectionRange(capturedPos, capturedPos);
                            setCursorPosition(capturedPos);
                          }
                        }, 0);

                        return newValue;
                      }
                      return prevValue;
                    });
                    return;
                  }

                  // AÇÃO: Enter (ligar)
                  if (key === 'Enter') {
                    e.preventDefault();
                    void handleCall();
                    return;
                  }

                  // NAVEGAÇÃO: Permitir setas, Home, End
                  if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
                    return; // Deixar o comportamento padrão
                  }

                  // SELEÇÃO: Permitir Ctrl/Cmd + A, C, V, X
                  if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(key.toLowerCase())) {
                    return; // Deixar o comportamento padrão
                  }

                  // NÚMEROS: Permitir apenas 0-9, * e #
                  if (/^[0-9*#]$/.test(key)) {
                    e.preventDefault();
                    handleDial(key);
                    return;
                  }

                  // BLOQUEAR: Tudo que não é número, ação ou navegação
                  e.preventDefault();
                }}
                onPaste={handlePaste}
                disabled={!miniCanUseKeypad}
                placeholder="Digite ou cole um numero"
                className={tw`flex-1 bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400 placeholder:text-xs placeholder:font-normal disabled:opacity-60 disabled:cursor-not-allowed`}
              />
              <button
                type="button"
                onClick={handleBackspace}
                disabled={!miniCanUseKeypad}
                className={tw`inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 transition hover:border-slate-400 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed`}
                aria-label="Apagar ultimo digito"
              >
                <FiDelete className={tw`text-base`} />
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={!miniCanUseKeypad}
                className={tw`inline-flex h-8 w-8 items-center justify-center rounded-lg border border-rose-300 bg-rose-50 text-rose-600 transition hover:border-rose-400 hover:bg-rose-100 disabled:opacity-40 disabled:cursor-not-allowed`}
                aria-label="Limpar numero"
              >
                <FiX className={tw`text-sm`} />
              </button>
            </div>
          </div>
        )}

        <div className={tw`grid grid-cols-3 gap-2`}>
          {keypadKeys.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => handleDial(item.value)}
              disabled={!miniCanUseKeypad}
              className={tw`flex h-12 flex-col items-center justify-center rounded-lg border border-slate-200 bg-white text-base font-semibold text-slate-800 shadow-sm transition active:scale-95 hover:border-slate-300 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <span>{item.value}</span>
              {item.detail && <span className={tw`text-[8px] font-semibold uppercase tracking-widest text-slate-400`}>{item.detail}</span>}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => {
            if (isCallActive) {
              void handleHangup();
            } else {
              void handleCall();
            }
          }}
          disabled={callButtonDisabled}
          className={tw`${isCallActive ? 'bg-rose-500 hover:bg-rose-600' : 'bg-green-500 hover:bg-green-600'} flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold text-white shadow-md transition disabled:cursor-not-allowed disabled:opacity-60`}
        >
          <FiPhone className={tw`text-base`} />
          {isCallActive ? 'Encerrar' : 'Ligar'}
        </button>

        {(callError || availabilityError || audioDeviceError) && (
          <div className={tw`rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-2 text-[11px] text-rose-700`}>
            <FiAlertTriangle className={tw`mr-1 inline text-xs`} />
            {callError || availabilityError || audioDeviceError}
          </div>
        )}

        {breakModal}
      </div>
    );
  }

  return (
    <div
      ref={dialerCardRef}
      tabIndex={0}
      role="application"
      aria-label="Discador manual"
      onFocusCapture={handleFocusWithin}
      onBlurCapture={handleBlurWithin}
      className={tw`space-y-3 rounded-2xl outline-none`}
    >
      <div className={tw`flex items-center justify-between gap-2`}>
        <div className={tw`flex items-center gap-2 min-w-0 flex-1`}>
          <span className={tw`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0`}>
            <FiUser className={tw`text-sm`} />
          </span>
          <div className={tw`min-w-0 flex-1`}>
            <p className={tw`text-sm font-bold text-slate-800 truncate`}>{username}</p>
            <p className={tw`text-[9px] uppercase tracking-wide text-slate-500 font-medium`}>Operador</p>
          </div>
        </div>
        <div className={tw`flex items-center gap-2 shrink-0`}>
          {isIdle && (
            <button
              type="button"
              onClick={() => setIsBreakModalOpen(true)}
              className={tw`inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700 transition-all hover:bg-amber-100 hover:shadow-sm`}
              aria-label="Entrar em pausa"
            >
              <FiPauseCircle className={tw`text-xs`} />
              <span>Pausa</span>
            </button>
          )}
          {isOnBreak && (
            <button
              type="button"
              onClick={handleGoOffBreak}
              className={tw`inline-flex items-center gap-1 rounded-lg border border-green-300 bg-green-50 px-2 py-1 text-[10px] font-bold text-green-700 transition-all hover:bg-green-100 hover:shadow-sm`}
              aria-label="Sair da pausa"
            >
              <FiCheckCircle className={tw`text-xs`} />
              <span>Voltar</span>
            </button>
          )}
          <div
            className={tw`${statusVisual.badgeClass} inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[10px] font-bold ${isInCall ? 'animate-pulse' : ''}`}
          >
            <statusVisual.Icon className={tw`text-xs`} />
            <span>{statusLabel}</span>
            <span className={tw`${statusVisual.dotClass} inline-flex h-1.5 w-1.5 rounded-full`} aria-hidden />
          </div>
        </div>
      </div>

      {showActiveSummary && (
        <>
          <ActiveCallSummary
            statusLabel={statusLabel}
            callTarget={callTarget}
            durationSeconds={callDurationSeconds}
          />
          <CallInfoDisplay />
        </>
      )}

      {/* Hide dialer display during active calls */}
      {!showActiveSummary && (
        <div
          ref={dialDisplayRef}
          className={tw`${isDialerFocused ? 'ring-2 ring-primary/50 shadow-lg' : 'shadow-sm'} relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3 transition-all hover:shadow-md`}
          onClick={() => dialerCardRef.current?.focus()}
        >
          <input
            ref={hiddenInputRef}
            id="dialer-number"
            type="tel"
            inputMode="tel"
            value={sanitizedDialValue}
            onChange={(event) => setDialValue(event.target.value)}
            onPaste={handlePaste}
            className={tw`sr-only`}
            tabIndex={-1}
            aria-hidden="true"
          />
          <div className={tw`flex items-center justify-between mb-2`}>
            <div className={tw`flex items-center gap-1.5`}>
              <FiPhone className={tw`text-sm text-primary`} />
              <span className={tw`text-[10px] uppercase tracking-wider text-slate-500 font-semibold`}>N�mero</span>
            </div>
            {isDialerFocused && (
              <span className={tw`inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-semibold text-primary`}>
                <span className={tw`inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse`}></span>
                Ativo
              </span>
            )}
          </div>
          <div className={tw`min-h-[2.5rem] flex items-center`}>
            <p className={tw`text-2xl font-semibold tabular-nums tracking-tight text-slate-800 flex-1 truncate`}>
              {dialDisplayText || (
                <span className={tw`text-slate-400 text-lg font-normal`}>Digite um n�mero</span>
              )}
            </p>
          </div>
        </div>
      )}

      <div className={tw`grid grid-cols-3 gap-2`}>
        {keypadKeys.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => handleDial(item.value)}
            disabled={!canUseKeypad}
            className={tw`relative flex h-14 flex-col items-center justify-center rounded-lg border border-slate-200 bg-gradient-to-b from-white to-slate-50 text-lg font-bold text-slate-800 shadow-sm transition-all active:scale-95 hover:border-slate-300 hover:shadow-md outline-none disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <span>{item.value}</span>
            {item.detail && <span className={tw`text-[8px] font-semibold tracking-widest text-slate-400 uppercase`}>{item.detail}</span>}
          </button>
        ))}
      </div>

      <div className={tw`space-y-2`}>
        <div className={tw`flex flex-wrap items-center gap-2`}>
          {(isIdle || isOnBreak) ? (
            <button
              type="button"
              onClick={handleCall}
              disabled={!isNumberValid}
              className={tw`flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-lg bg-green-500 px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-green-600 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-lg`}
            >
              <FiPhone className={tw`text-base`} />
              Ligar
            </button>
          ) : isIncoming ? (
            <>
              <button
                type="button"
                onClick={handleReject}
                className={tw`flex flex-1 min-w-[6rem] items-center justify-center gap-1.5 rounded-lg bg-rose-500 px-3 py-2 text-xs font-bold text-white shadow-md transition-all hover:bg-rose-600 hover:shadow-lg`}
              >
                <FiPhoneOff className={tw`text-sm`} />
                Recusar
              </button>
              <button
                type="button"
                onClick={handleAnswer}
                className={tw`flex flex-1 min-w-[6rem] items-center justify-center gap-1.5 rounded-lg bg-green-500 px-3 py-2 text-xs font-bold text-white shadow-md transition-all hover:bg-green-600 hover:shadow-lg`}
              >
                <FiPhoneIncoming className={tw`text-sm`} />
                Atender
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleHangup}
              className={tw`flex flex-1 min-w-[7rem] items-center justify-center gap-2 rounded-lg bg-rose-500 px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-rose-600 hover:shadow-xl`}
            >
              <FiPhoneOff className={tw`text-base`} />
              Encerrar
            </button>
          )}

          <button
            type="button"
            onClick={() => setInputMuted(!inputMuted)}
            className={tw`${inputMuted ? 'border-rose-300 bg-rose-50 text-rose-600' : 'border-slate-300 bg-white text-slate-700'} flex h-9 min-w-[4.5rem] flex-1 items-center justify-center gap-1 rounded-lg border px-2 py-1.5 text-[10px] font-bold transition-all hover:border-slate-400 hover:shadow-md disabled:cursor-not-allowed`}
          >
            <MicIcon className={tw`text-xs`} />
            {inputMuted ? 'Mudo' : 'Mic'}
          </button>

          <button
            type="button"
            onClick={() => setOutputMuted(!outputMuted)}
            className={tw`${outputMuted ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-300 bg-white text-slate-700'} flex h-9 min-w-[4.5rem] flex-1 items-center justify-center gap-1 rounded-lg border px-2 py-1.5 text-[10px] font-bold transition-all hover:border-slate-400 hover:shadow-md`}
          >
            <SpeakerIcon className={tw`text-xs`} />
            {outputMuted ? 'Mudo' : `${volumePercent}%`}
          </button>

          <button
            type="button"
            onClick={handleBackspace}
            className={tw`flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 transition-all hover:border-slate-400 hover:shadow-md`}
            aria-label="Apagar ultimo digito"
          >
            <FiDelete className={tw`text-base`} />
          </button>

          <button
            type="button"
            onClick={handleClear}
            className={tw`flex h-9 w-9 items-center justify-center rounded-lg border border-rose-300 bg-rose-50 text-rose-700 font-bold text-xs transition-all hover:border-rose-400 hover:bg-rose-100 hover:shadow-md active:bg-rose-200`}
            aria-label="Limpar numero"
          >
            C
          </button>
        </div>

        <div className={tw`rounded-xl border border-slate-200 bg-white/80 p-3 shadow-sm`}>
          <div className={tw`flex flex-wrap items-center justify-between gap-2`}>
            <div>
              <p className={tw`text-[10px] uppercase tracking-wide text-slate-500`}>Volume</p>
              <p className={tw`text-sm font-semibold text-slate-800`}>Sa�da {volumePercent}%</p>
            </div>
            <div className={tw`flex items-center gap-2`}>
              <button
                type="button"
                onClick={() => setVolumeClamped((outputVolume ?? 0) - 0.05)}
                className={tw`inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-primary/60 hover:text-primary`}
                aria-label="Diminuir volume"
              >
                <FiMinus className={tw`text-sm`} />
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={Number.isFinite(outputVolume) ? outputVolume : 0}
                onChange={(event) => setVolumeClamped(parseFloat(event.target.value))}
                className={tw`h-1 w-full max-w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-primary`}
                aria-label="Volume de saida"
              />
              <button
                type="button"
                onClick={() => setVolumeClamped((outputVolume ?? 0) + 0.05)}
                className={tw`inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-primary/60 hover:text-primary`}
                aria-label="Aumentar volume"
              >
                <FiPlus className={tw`text-sm`} />
              </button>
            </div>
          </div>

          <div className={tw`mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600`}>
            <div className={tw`flex items-center gap-1.5`}>
              <FiMic className={tw`text-sm text-primary`} />
              <span className={tw`truncate max-w-full`}>{inputDeviceLabel}</span>
            </div>
            <div className={tw`flex items-center gap-1.5`}>
              <FiHeadphones className={tw`text-sm text-primary`} />
              <span className={tw`truncate max-w-full`}>{outputDeviceLabel}</span>
            </div>
            {(audioDevices.inputs.length > 0 || audioDevices.outputs.length > 0) && (
              <button
                type="button"
                onClick={() => setIsDevicePanelOpen((prev) => !prev)}
                className={tw`inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[9px] font-bold text-slate-700 transition-all hover:border-slate-400 hover:shadow-sm`}
              >
                <FiSettings className={tw`text-[10px]`} />
                {isDevicePanelOpen ? 'Ocultar' : 'Config'}
              </button>
            )}
          </div>

          {isDevicePanelOpen && (
            <div className={tw`mt-3 grid gap-2`}>
              <label className={tw`space-y-1 text-xs text-slate-700`}>
                <span className={tw`flex items-center gap-1.5 font-semibold`}>
                  <FiMic className={tw`text-sm text-primary`} />
                  Entrada
                </span>
                <select
                  className={tw`w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40`}
                  value={selectedInputId ?? ''}
                  onChange={handleSelectInputDevice}
                >
                  {audioDevices.inputs.length === 0 && <option value="">Padr�o</option>}
                  {audioDevices.inputs.map((device) => (
                    <option key={device.deviceId || device.label} value={device.deviceId}>
                      {device.label || 'Padr�o'}
                    </option>
                  ))}
                </select>
              </label>

              <label className={tw`space-y-1 text-xs text-slate-700`}>
                <span className={tw`flex items-center gap-1.5 font-semibold`}>
                  <FiHeadphones className={tw`text-sm text-primary`} />
                  Sa�da
                </span>
                <select
                  className={tw`w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40`}
                  value={selectedOutputId ?? ''}
                  onChange={handleSelectOutputDevice}
                >
                  {audioDevices.outputs.length === 0 && <option value="">Padr�o</option>}
                  {audioDevices.outputs.map((device) => (
                    <option key={device.deviceId || device.label} value={device.deviceId}>
                      {device.label || 'Padr�o'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      </div>

      {(callError || availabilityError || audioDeviceError) && (
        <div className={tw`space-y-1.5`}>
          {callError && (
            <div className={tw`flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-2.5 py-2 text-[11px] text-rose-700`}>
              <FiAlertTriangle className={tw`mt-0.5 text-xs`} />
              <span>{callError}</span>
            </div>
          )}
          {availabilityError && (
            <div className={tw`flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-700`}>
              <FiAlertTriangle className={tw`mt-0.5 text-xs`} />
              <span>{availabilityError}</span>
            </div>
          )}
          {audioDeviceError && (
            <div className={tw`flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-2.5 py-2 text-[11px] text-sky-700`}>
              <FiAlertTriangle className={tw`mt-0.5 text-xs`} />
              <span>{audioDeviceError}</span>
            </div>
          )}
        </div>
      )}

      {breakModal}


      <button
        type="button"
        onClick={onLogout}
        className={tw`flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 transition hover:border-red-400 hover:text-red-500`}
      >
        <FiLogOut className={tw`text-base`} />
        Sair
      </button>
    </div>
  );
}

function formatDialerDisplay(rawValue: string, defaultPrefix: string): string {
  const sanitized = sanitizeDialInput(rawValue);
  if (!sanitized) {
    return '';
  }

  const hasExplicitPrefix = sanitized.startsWith('+');
  const numeric = sanitized.replace(/[^0-9]/g, '');
  const defaultDigits = defaultPrefix.replace(/[^0-9]/g, '');
  const expectedCountryLength = defaultDigits.length || 2;
  const derivedCountry = hasExplicitPrefix
    ? numeric.slice(0, Math.min(expectedCountryLength, numeric.length))
    : defaultDigits || numeric.slice(0, expectedCountryLength);

  let localDigits = hasExplicitPrefix ? numeric.slice(derivedCountry.length) : numeric;
  const countryPrefix = hasExplicitPrefix ? `+${derivedCountry}` : defaultPrefix;

  if (derivedCountry === '55' && localDigits.length >= 8) {
    const area = localDigits.slice(0, 2);
    const core = localDigits.slice(2, Math.max(localDigits.length - 4, 2));
    const tail = localDigits.slice(-4);
    return `${countryPrefix} (${area}) ${core}-${tail}`.replace(/\s+/g, ' ').trim();
  }

  if (!localDigits) {
    return countryPrefix.trim();
  }

  const groups: string[] = [];
  while (localDigits.length > 4) {
    groups.push(localDigits.slice(0, 3));
    localDigits = localDigits.slice(3);
  }
  if (localDigits) {
    groups.push(localDigits);
  }

  const formatted = groups.join(' ');
  return `${countryPrefix} ${formatted}`.replace(/\s+/g, ' ').trim();
}

function sanitizeDialInput(value: string): string {
  if (!value) {
    return '';
  }

  const normalized = value.normalize('NFKC');
  let result = '';

  for (const char of normalized) {
    if (/^[0-9*#]$/.test(char)) {
      result += char;
      continue;
    }

    if (char === '+' && result.length === 0) {
      result += char;
    }
  }

  return result;
}

function normalizeToE164(rawValue: string, defaultPrefix: string): string | null {
  const sanitized = sanitizeDialInput(rawValue);
  if (!sanitized) {
    return null;
  }

  if (sanitized.startsWith('+')) {
    return sanitized.replace(/[^0-9+]/g, '');
  }

  const digits = sanitized.replace(/[^0-9]/g, '');
  if (!digits) {
    return null;
  }

  const prefixDigits = defaultPrefix.replace(/[^0-9]/g, '');
  if (!prefixDigits) {
    return `+${digits}`;
  }

  // Para números brasileiros (código do país 55), verifica se precisa adicionar o 9
  let phoneNumber = digits;
  if (prefixDigits === '55' && digits.length >= 10) {
    // Extrai DDD (2 primeiros dígitos) e o restante
    const ddd = digits.substring(0, 2);
    const number = digits.substring(2);

    // Se o número tem 8 dígitos e não começa com 2, 3 ou 4, adiciona o 9
    if (number.length === 8 && !['2', '3', '4'].includes(number[0])) {
      phoneNumber = `${ddd}9${number}`;
    }
  }

  return `+${prefixDigits}${phoneNumber}`;
}

function isValidPhoneNumberSafe(value: string): boolean {
  try {
    return isValidPhoneNumber(value);
  } catch (error) {
    console.warn('Falha ao validar numero', error);
    return false;
  }
}

function toRecord(value: unknown): UnknownRecord | null {
  if (value && typeof value === 'object') {
    return value as UnknownRecord;
  }

  return null;
}

function getRecordValue<T>(value: unknown, key: string): T | undefined {
  const record = toRecord(value);
  if (!record || !(key in record)) {
    return undefined;
  }

  return record[key] as T;
}

function getRecordFunction<Args extends unknown[], Return>(value: unknown, key: string): ((...args: Args) => Return) | undefined {
  const candidate = getRecordValue<unknown>(value, key);
  return typeof candidate === 'function' ? (candidate as (...args: Args) => Return) : undefined;
}

function extractPeerConnection(source: unknown): RTCPeerConnection | undefined {
  const session =
    getRecordValue<unknown>(source, 'rtcSession') ??
    getRecordValue<unknown>(source, '_session') ??
    getRecordValue<unknown>(source, 'session');

  const sessionConnection =
    getRecordValue<RTCPeerConnection | null>(session, '_connection') ??
    getRecordValue<RTCPeerConnection | null>(session, 'connection');

  if (sessionConnection) {
    return sessionConnection;
  }

  const directConnection = getRecordValue<RTCPeerConnection | null>(source, 'pc');
  return directConnection ?? undefined;
}

function isCallCounterpart(value: unknown): value is CallCounterpart {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  const displayName = record.displayName;
  const phoneNumber = record.phoneNumber;

  const displayNameValid = displayName == null || typeof displayName === 'string';
  const phoneNumberValid = phoneNumber == null || typeof phoneNumber === 'string';

  return displayNameValid && phoneNumberValid;
}

function extractCounterpart(call: Call | undefined): CallCounterpart | null {
  const candidate = getRecordValue<unknown>(call, 'counterpart');
  return isCallCounterpart(candidate) ? candidate : null;
}

function readNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

function formatPercent(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`;
}

const DTMF_FREQUENCIES = {
  '1': [1209, 697],
  '2': [1336, 697],
  '3': [1477, 697],
  '4': [1209, 770],
  '5': [1336, 770],
  '6': [1477, 770],
  '7': [1209, 852],
  '8': [1336, 852],
  '9': [1477, 852],
  '*': [1209, 941],
  '0': [1336, 941],
  '#': [1477, 941],
} as const;

type StatusVisualConfig = {
  Icon: IconType;
  badgeClass: string;
  dotClass: string;
};

function getStatusVisual(state?: string): StatusVisualConfig {
  switch (state) {
    case 'idle':
      return {
        Icon: FiCheckCircle,
        badgeClass: tw`border-green-200 bg-green-50 text-green-700`,
        dotClass: tw`bg-green-500`,
      };
    case 'callInProgress':
    case 'manualCallSetup':
    case 'manualCallRinging':
    case 'callRinging':
      return {
        Icon: FiActivity,
        badgeClass: tw`border-primary/30 bg-primary/10 text-primary`,
        dotClass: tw`bg-primary`,
      };
    case 'onBreak':
      return {
        Icon: FiPauseCircle,
        badgeClass: tw`border-amber-200 bg-amber-50 text-amber-700`,
        dotClass: tw`bg-amber-500`,
      };
    case 'offline':
    case 'error':
      return {
        Icon: FiAlertTriangle,
        badgeClass: tw`border-rose-200 bg-rose-50 text-rose-700`,
        dotClass: tw`bg-rose-500`,
      };
    default:
      return {
        Icon: FiPhoneCall,
        badgeClass: tw`border-slate-200 bg-slate-50 text-slate-600`,
        dotClass: tw`bg-slate-500`,
      };
  }
}

function clamp(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) {
    return min;
  }

  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

function getStatusLabel(state?: string) {
  switch (state) {
    case 'starting':
      return 'Conectando';
    case 'idle':
      return 'Disponivel';
    case 'manualCallSetup':
      return 'Preparando';
    case 'manualCallRinging':
      return 'Chamando';
    case 'callRinging':
      return 'Recebida';
    case 'callInProgress':
      return 'Em chamada';
    case 'afterCall':
      return 'Finalizando';
    case 'onBreak':
      return 'Em pausa';
    case 'offline':
      return 'Offline';
    case 'error':
      return 'Erro';
    default:
      return 'Aguardando';
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function evaluateNetworkQuality(metric: NetworkMetricState): QualitySummary {
  const { rttMs, bitrateKbps, packetLossRatio } = metric;
  const details: string[] = [];

  if (bitrateKbps != null) {
    details.push(`${Math.round(bitrateKbps)} kbps`);
  }

  if (packetLossRatio != null) {
    details.push(`${formatPercent(packetLossRatio)} perda`);
  }

  if (rttMs != null) {
    details.push(`${Math.round(rttMs)} ms RTT`);
  }

  if (details.length === 0) {
    return {
      tone: 'unknown',
      label: 'Sem dados',
      message: 'Aguardando m�tricas de rede.',
    };
  }

  const loss = packetLossRatio ?? 0;
  const latency = rttMs ?? 0;
  const bitrate = bitrateKbps ?? 0;
  const message = details.join(' � ');

  if (loss > 0.08 || latency > 350 || bitrate < 20) {
    return { tone: 'poor', label: 'Ruim', message };
  }

  if (loss > 0.03 || latency > 220 || bitrate < 40) {
    return { tone: 'average', label: 'Inst�vel', message };
  }

  if (loss > 0.01 || latency > 140 || bitrate < 60) {
    return { tone: 'good', label: 'Boa', message };
  }

  return { tone: 'excellent', label: 'Excelente', message };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function evaluateAudioQuality(metric: AudioMetricState): QualitySummary {
  const { jitterMs, packetLossRatio, audioLevel } = metric;
  const details: string[] = [];

  if (audioLevel != null) {
    details.push(`N�vel ${(audioLevel * 100).toFixed(0)}%`);
  }

  if (packetLossRatio != null) {
    details.push(`${formatPercent(packetLossRatio)} perda`);
  }

  if (jitterMs != null) {
    details.push(`${Math.round(jitterMs)} ms jitter`);
  }

  if (details.length === 0) {
    return {
      tone: 'unknown',
      label: 'Sem dados',
      message: 'Aguardando m�tricas de �udio.',
    };
  }

  const loss = packetLossRatio ?? 0;
  const jitter = jitterMs ?? 0;
  const level = audioLevel ?? 0.5;
  const message = details.join(' � ');

  if (loss > 0.08 || jitter > 60 || level < 0.05) {
    return { tone: 'poor', label: 'Ruim', message };
  }

  if (loss > 0.03 || jitter > 35 || level < 0.1) {
    return { tone: 'average', label: 'Inst�vel', message };
  }

  if (loss > 0.01 || jitter > 20) {
    return { tone: 'good', label: 'Boa', message };
  }

  return { tone: 'excellent', label: 'Excelente', message };
}

function formatDuration(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const remaining = (safeSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remaining}`;
}

type AfterCallPanelProps = {
  username: string;
  result: CallInteractionResult;
  onChangeResult: (result: CallInteractionResult) => void;
  successQualifications: CallQualification[];
  discardQualifications: CallQualification[];
  selectedQualificationId: number | null;
  onSelectQualification: (qualificationId: number) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  onLogout: () => void;
  isMobile?: boolean;
  onClose?: () => void;
};

function AfterCallPanel({
  username,
  result,
  onChangeResult,
  successQualifications,
  discardQualifications,
  selectedQualificationId,
  onSelectQualification,
  onSubmit,
  isSubmitting,
  errorMessage,
  onLogout,
  isMobile = false,
  onClose,
}: AfterCallPanelProps) {
  const options = result === 'success' ? successQualifications : discardQualifications;
  const hasOptions = options.length > 0;

  return (
    <div
      className={tw`${
        isMobile ? 'min-h-[70vh] rounded-3xl border border-slate-200 bg-white p-4 shadow-xl' : 'space-y-5'
      }`}
    >
      <div className={tw`mb-3 flex items-center justify-between`}>
        <p className={tw`text-xs uppercase tracking-wide text-slate-500`}>Logado como</p>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={tw`inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300`}
          >
            <FiArrowLeft className={tw`text-xs`} />
            Voltar
          </button>
        )}
      </div>

      <div className={tw`${isMobile ? 'space-y-3' : 'space-y-1'}`}>
        <p className={tw`text-sm font-semibold text-slate-800`}>{username}</p>
      </div>

      <div
        className={tw`space-y-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 ${
          isMobile ? 'shadow-inner' : ''
        }`}
      >
        <div>
          <p className={tw`text-sm font-semibold text-slate-700`}>Qualifique a chamada</p>
          <p className={tw`text-xs text-slate-500`}>
            Esta etapa e obrigatoria para registrar o desfecho do atendimento.
          </p>
        </div>

        <div className={tw`${isMobile ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 gap-2'}`}>
          <button
            type="button"
            onClick={() => onChangeResult('success')}
            className={tw`${result === 'success' ? 'bg-green-500 text-white shadow-lg' : 'bg-white text-slate-600'} rounded-xl border border-slate-200 px-3 py-3 text-sm font-semibold transition hover:border-green-400`}
          >
            Atendimento concluido
          </button>
          <button
            type="button"
            onClick={() => onChangeResult('failure')}
            className={tw`${result === 'failure' ? 'bg-rose-500 text-white shadow-lg' : 'bg-white text-slate-600'} rounded-xl border border-slate-200 px-3 py-3 text-sm font-semibold transition hover:border-rose-400`}
          >
            Atendimento descartado
          </button>
        </div>

        <div className={tw`space-y-2`}>
          <p className={tw`text-sm font-medium text-slate-700`}>Selecione uma qualificacao</p>
          {hasOptions ? (
            <div className={tw`${isMobile ? 'max-h-64' : 'max-h-52'} space-y-2 overflow-y-auto pr-1`}>
              {options.map((qualification) => {
                const isSelected = selectedQualificationId === qualification.id;
                return (
                  <button
                    key={qualification.id}
                    type="button"
                    onClick={() => onSelectQualification(qualification.id)}
                    className={tw`${isSelected ? 'ring-2 ring-primary bg-primary/5 border-primary' : 'border-slate-200 bg-white'} w-full rounded-xl border px-3 py-3 text-left text-sm font-semibold transition hover:border-primary`}
                  >
                    <p className={tw`text-sm font-semibold text-slate-800`}>{qualification.name}</p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={tw`rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700`}>
              Nenhuma qualificacao disponivel para este tipo de resultado.
            </div>
          )}
        </div>

        {errorMessage && <p className={tw`text-xs text-red-500`}>{errorMessage}</p>}

        <button
          type="button"
          onClick={onSubmit}
          disabled={!hasOptions || !selectedQualificationId || isSubmitting}
          className={tw`flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-primaryDark disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {isSubmitting && (
            <span className={tw`inline-flex h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white`} />
          )}
          Finalizar atendimento
        </button>
      </div>

      <button
        type="button"
        onClick={onLogout}
        className={tw`flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-red-400 hover:text-red-500`}
      >
        <FiLogOut className={tw`text-base`} />
        Sair
      </button>
    </div>
  );
}

type ActiveCallSummaryProps = {
  statusLabel: string;
  callTarget?: string;
  durationSeconds: number;
};

function ActiveCallSummary({
  statusLabel,
  callTarget,
  durationSeconds,
}: ActiveCallSummaryProps) {
  const durationLabel = durationSeconds > 0 ? formatDuration(durationSeconds) : '00:00';
  
  // Remove o +55 do n�mero se existir
  const cleanNumber = callTarget?.replace(/^\+55/, '') || 'Conectando...';

  return (
    <div className={tw`rounded-lg border border-green-200 bg-gradient-to-br from-green-50 to-white px-3 py-2`}>
      <div className={tw`flex items-center justify-between gap-3`}>
        {/* �cone e N�mero */}
        <div className={tw`flex items-center gap-2.5 min-w-0 flex-1`}>
          <div className={tw`flex items-center justify-center w-8 h-8 rounded-full bg-green-500 shrink-0 ${durationSeconds > 0 ? 'animate-pulse' : ''}`}>
            <FiPhoneCall className={tw`text-sm text-white`} />
          </div>
          <div className={tw`flex flex-col gap-0.5 min-w-0 flex-1`}>
            <span className={tw`text-base font-bold text-slate-800 truncate font-mono`}>
              {cleanNumber}
            </span>
            <span className={tw`text-[9px] font-medium text-green-600 uppercase tracking-wider`}>
              {statusLabel}
            </span>
          </div>
        </div>
        
        {/* Timer */}
        {durationSeconds > 0 && (
          <div className={tw`flex items-center gap-1.5 bg-green-500 rounded-lg px-2.5 py-1.5 shrink-0`}>
            <FiClock className={tw`text-xs text-white`} />
            <span className={tw`text-sm font-bold tabular-nums text-white`}>{durationLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
}



